'use client';

import { useState, useRef, useCallback, useContext, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import {
  ArrowLeftIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  UserPlusIcon,
  SparklesIcon,
  CheckIcon,
  LinkIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  HeartIcon,
  ChatBubbleLeftEllipsisIcon,
  StarIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import BrainstormJobStatus from './BrainstormJobStatus';

// ─── Constants ────────────────────────────────────────────────────────────────
const GRID_SIZE = 40;
const CANVAS_WIDTH = 3200;
const CANVAS_HEIGHT = 2000;
const GRID_COLS = 16;
const GRID_ROWS = 10;

const PRESET_COLORS = [
  { label: 'Yellow',  bg: '#fef08a', border: '#ca8a04', text: '#713f12' },
  { label: 'Rose',    bg: '#fecdd3', border: '#e11d48', text: '#881337' },
  { label: 'Sky',     bg: '#bae6fd', border: '#0284c7', text: '#0c4a6e' },
  { label: 'Green',   bg: '#bbf7d0', border: '#16a34a', text: '#14532d' },
  { label: 'Purple',  bg: '#e9d5ff', border: '#9333ea', text: '#581c87' },
  { label: 'Orange',  bg: '#fed7aa', border: '#ea580c', text: '#7c2d12' },
];

const snapToGrid = (val) => Math.round(val / GRID_SIZE) * GRID_SIZE;

// ─── AI Agents ────────────────────────────────────────────────────────────────
const AI_AGENTS = [
  {
    id: 'brand_identity',
    emoji: '🎯',
    title: 'Brand Identity Agent',
    description: 'Builds the core identity system of the brand.',
    inputs: [
      { key: 'websiteUrl',        label: 'Website URL',        type: 'url',      placeholder: 'https://yourbrand.com',                         optional: true  },
      { key: 'brandDescription',  label: 'Brand Description',  type: 'textarea', placeholder: 'Describe your brand, values and mission...',     optional: false },
      { key: 'industry',          label: 'Industry / Niche',   type: 'text',     placeholder: 'e.g. Fashion, SaaS, Food...',                   optional: true  },
      { key: 'referenceFile',     label: 'Reference Documents', type: 'file',                                                                  optional: true  },
    ],
    outputs: ['Brand DNA document', 'Tone of voice rules', 'Messaging pillars', 'Positioning statement'],
  },
  {
    id: 'marketing_strategy',
    emoji: '🚀',
    title: 'Marketing Strategy Agent',
    description: 'Understands the business and creates a full marketing direction.',
    inputs: [
      { key: 'productDescription', label: 'Product or Service Description', type: 'textarea', placeholder: 'Describe your product or service...',          optional: false },
      { key: 'websiteUrl',         label: 'Website URL',                    type: 'url',      placeholder: 'https://yourbusiness.com',                     optional: true  },
      { key: 'targetAudience',     label: 'Target Audience',                type: 'text',     placeholder: 'e.g. 25-40 year old entrepreneurs',            optional: true  },
      { key: 'goalType',           label: 'Goal Type',                      type: 'select',   options: ['launch new product', 'grow existing product', 'reposition brand'], optional: false },
      { key: 'contextPrompt',      label: 'What do you want to achieve?',   type: 'textarea', placeholder: 'Optional additional context...',               optional: true  },
      { key: 'referenceFile',      label: 'Reference Documents',            type: 'file',                                                                  optional: true  },
    ],
    outputs: ['Marketing strategy map', 'Campaign or launch plan', 'Key messaging angles', 'Creative direction summary'],
  },
  {
    id: 'copywriting',
    emoji: '✍️',
    title: 'Copywriting Agent',
    description: 'Generates all written content in brand voice.',
    inputs: [
      { key: 'websiteUrl',     label: 'Company Website URL',    type: 'url',      placeholder: 'https://yourbrand.com',                optional: true  },
      { key: 'requestType',    label: 'Content Type',           type: 'select',   options: ['ads', 'landing page copy', 'social posts', 'product descriptions', 'blog posts'], optional: false },
      { key: 'brandVoice',     label: 'Tone Adjustment',        type: 'text',     placeholder: 'e.g. more luxury, bold, minimal...',  optional: true  },
      { key: 'contextPrompt',  label: 'What is this copy for?', type: 'textarea', placeholder: 'Describe the product, campaign or context...', optional: false },
      { key: 'referenceFile',  label: 'Reference Documents',    type: 'file',     optional: true },
    ],
    outputs: ['Copy variations', 'Headlines + hooks', 'Platform-specific messaging sets'],
  },
  {
    id: 'landing_page',
    emoji: '🌐',
    title: 'Landing Page Agent',
    description: 'Builds structured landing page logic with conversion narrative.',
    inputs: [
      { key: 'productDescription', label: 'Product / Service Description',    type: 'textarea', placeholder: 'What are you selling?',             optional: false },
      { key: 'websiteUrl',         label: 'Website URL (if redesigning)',      type: 'url',      placeholder: 'https://yoursite.com',               optional: true  },
      { key: 'competitorUrls',     label: 'Competitor URLs',                  type: 'text',     placeholder: 'https://competitor.com, ...',       optional: true  },
      { key: 'referenceFile',      label: 'Reference Documents',              type: 'file',                                                       optional: true  },
    ],
    outputs: ['Page structure (sections)', 'Full written copy per section', 'CTA hierarchy', 'Conversion narrative flow'],
  },
  {
    id: 'seo_opportunity',
    emoji: '🔍',
    title: 'SEO Opportunity Agent',
    description: 'Finds missing content opportunities and intent gaps.',
    inputs: [
      { key: 'websiteUrl',      label: 'Website URL',        type: 'url',  placeholder: 'https://yoursite.com',                          optional: false },
      { key: 'industry',        label: 'Industry / Niche',   type: 'text', placeholder: 'e.g. Digital marketing agency',                optional: true  },
      { key: 'competitorUrls',  label: 'Competitor URLs',    type: 'text', placeholder: 'https://competitor.com, ...',                  optional: true  },
      { key: 'targetKeywords',  label: 'Target Keywords',    type: 'text', placeholder: 'e.g. social media management, content marketing', optional: true },
      { key: 'referenceFile',   label: 'Reference Documents', type: 'file',                                                              optional: true },
    ],
    outputs: ['SEO content gap analysis', 'Blog / landing page ideas', 'Content clusters'],
  },
  {
    id: 'social_media',
    emoji: '📱',
    title: 'Social Media Content Agent',
    description: 'Turns strategy into distribution-ready content.',
    inputs: [
      { key: 'websiteUrl',    label: 'Company Website URL', type: 'url',         placeholder: 'https://yourbrand.com',                optional: true  },
      { key: 'campaignType',  label: 'Campaign Type',      type: 'select',      options: ['launch', 'ongoing content'],              optional: false },
      { key: 'platforms',     label: 'Platforms',          type: 'multiselect', options: ['LinkedIn', 'Instagram', 'TikTok'],        optional: false },
      { key: 'tonePrompt',    label: 'Tone / Style',       type: 'text',        placeholder: 'e.g. viral, professional, storytelling', optional: true },
      { key: 'contextPrompt', label: 'Campaign Context',   type: 'textarea',    placeholder: 'What is this content campaign about?', optional: false },
      { key: 'referenceFile', label: 'Reference Documents', type: 'file',                                                             optional: true },
    ],
    outputs: ['Post sets per platform', 'Content series ideas', 'Campaign post sequences'],
  },
];

// ─── Dot-grid canvas background ───────────────────────────────────────────────
const DOT_BG = {
  backgroundImage: 'radial-gradient(circle, #94a3b8 1.5px, transparent 1.5px)',
  backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
  backgroundPosition: '0 0',
};

const MONGO_BASE = process.env.REACT_APP_MONGO_URL || 'https://analytics.insightos.co.uk' || 'https://analytics.insightos.co.uk';

const resolveApiBase = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (typeof window !== 'undefined') {
    const host = String(window.location?.hostname || '').toLowerCase();
    const isLocalHost = host === 'localhost' || host === '127.0.0.1';
    return isLocalHost ? 'https://api.insightos.co.uk' : 'https://api.insightos.co.uk';
  }

  return 'https://api.insightos.co.uk';
};

const API_BASE = resolveApiBase();
const BRAINSTORM_REALTIME_ENABLED =
  (process.env.REACT_APP_BRAINSTORM_REALTIME_ENABLED || 'true').toLowerCase() !== 'false';

const getBrainstormWsUrl = () => {
  try {
    const base = new URL(MONGO_BASE);
    const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${base.host}/ws/brainstorm`;
  } catch {
    return 'ws://localhost:4657/ws/brainstorm';
  }
};

const sanitizeInvited = (input) => (
  Array.isArray(input)
    ? input
        .map((entry) => ({
          id: Number(entry?.id),
          name: (entry?.name || '').toString().trim(),
        }))
        .filter((entry) => Number.isFinite(entry.id) && entry.name)
    : []
);

const resolveApiAssetUrl = (url) => {
  if (!url) return '';
  const normalized = String(url).trim();
  if (!normalized) return '';
  if (/^https?:\/\//i.test(normalized)) return normalized;
  if (normalized.startsWith('/api/')) {
    if (API_BASE) {
      return `${API_BASE}${normalized}`;
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${normalized}`;
    }

    return normalized;
  }
  return normalized;
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function Brainstorm({ projectId, projectName, brainstormId }) {
  const navigate = useNavigate();
  const user = useContext(AuthContext);
  const currentUserDisplayName =
    user?.name ||
    user?.Name ||
    user?.username ||
    user?.user?.name ||
    user?.user?.Name ||
    'Unknown';

  // Canvas elements
  const [elements, setElements]       = useState([]);
  const [boardTitle, setBoardTitle]   = useState('Brainstorm Board');
  const [selectedId, setSelectedId]   = useState(null);
  const [activeColor, setActiveColor] = useState(PRESET_COLORS[0]);
  const [isLoadingBoard, setIsLoadingBoard] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const [viewMode, setViewMode] = useState('canvas');
  const [brainstormOptions, setBrainstormOptions] = useState([]);
  const [isLoadingBrainstorms, setIsLoadingBrainstorms] = useState(false);
  const [selectedBrainstormId, setSelectedBrainstormId] = useState(brainstormId || '');

  // Add-shape flow
  const [pendingType, setPendingType]       = useState(null);
  const [gridPickerOpen, setGridPickerOpen] = useState(false);
  const [hoverCell, setHoverCell]           = useState(null);

  // Detail editor
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Doc editor
  const [docOpen, setDocOpen] = useState(false);
  const [docData, setDocData] = useState(null);
  const [docPromptOpen, setDocPromptOpen] = useState(false);
  const [docPrompt, setDocPrompt] = useState('');
  const [docPromptLoading, setDocPromptLoading] = useState(false);
  const [docPromptError, setDocPromptError] = useState('');
  const [docUseWebSearch, setDocUseWebSearch] = useState(false);

  // Picture editor
  const [pictureOpen, setPictureOpen] = useState(false);
  const [pictureData, setPictureData] = useState(null);
  const [picturePrompt, setPicturePrompt] = useState('');
  const [pictureError, setPictureError] = useState('');
  const [pictureUploading, setPictureUploading] = useState(false);
  const [pictureGenerating, setPictureGenerating] = useState(false);
  const [pictureJobId, setPictureJobId] = useState('');

  // Invite modal
  const [inviteOpen, setInviteOpen]     = useState(false);
  const [invitedStaff, setInvitedStaff] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteEmail, setInviteEmail]   = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);

  // Product owner & access control
  const [productOwner, setProductOwner] = useState(null); // { id, name } | null
  const [accessDenied, setAccessDenied] = useState(false);

  // Engagement modal (likes / comments / poll)
  const [engagementOpen, setEngagementOpen] = useState(false);
  const [engagementEl, setEngagementEl] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [engagementError, setEngagementError] = useState('');

  // Turn a board element into a scheduled WorkCoach action.
  const [todoElement, setTodoElement] = useState(null);
  const [todoForm, setTodoForm] = useState({ staffId: '', date: '', timeFrom: '', timeTo: '' });
  const [todoSubmitting, setTodoSubmitting] = useState(false);
  const [todoError, setTodoError] = useState('');

  // Picture modal extras
  const [pendingGeneratedUrl, setPendingGeneratedUrl] = useState('');
  const [pictureSectionTab, setPictureSectionTab] = useState('generate'); // 'generate' | 'upload'

  // New board modal
  const [newBoardOpen, setNewBoardOpen]       = useState(false);
  const [newBoardMode, setNewBoardMode]       = useState('blank'); // 'blank' | 'ai'
  const [newBoardTitle, setNewBoardTitle]     = useState('');
  const [selectedAgent, setSelectedAgent]     = useState(null);
  const [agentInputs, setAgentInputs]         = useState({});
  const [agentFiles, setAgentFiles]           = useState({}); // File[] keyed by field.key
  const [newBoardCreating, setNewBoardCreating] = useState(false);
  const [newBoardError, setNewBoardError]     = useState('');
  const [aiJobId, setAiJobId]               = useState(null);
  const [aiJobProgress, setAiJobProgress]   = useState(null);
  const [aiJobPolling, setAiJobPolling]     = useState(false);

  // Drag state (ref so we don't re-render mid-drag)
  const dragRef   = useRef(null);
  const canvasRef = useRef(null);
  const skipNextAutoSaveRef = useRef(true);
  const saveTimerRef = useRef(null);
  const wsRef = useRef(null);
  const localVersionRef = useRef(0);
  const suppressNextRealtimeSyncRef = useRef(false);
  const sentOpIdsRef = useRef(new Set());
  const pendingRealtimeSaveRef = useRef(null);
  const [realtimeState, setRealtimeState] = useState('idle');
  const [presenceUsers, setPresenceUsers] = useState([]);
  const effectiveBrainstormId = selectedBrainstormId || brainstormId || '';

  useEffect(() => {
    if (brainstormId) {
      setSelectedBrainstormId(brainstormId);
    }
  }, [brainstormId]);

  useEffect(() => {
    const loadBrainstorms = async () => {
      setIsLoadingBrainstorms(true);
      try {
        const response = await fetch(`${MONGO_BASE}/api/brainstorms`, {
          method: 'GET',
          credentials: 'include',
        });
        const payload = await response.json();

        if (!response.ok || !payload?.success) {
          throw new Error(payload?.message || 'Failed to load brainstorm list');
        }

        const options = Array.isArray(payload?.data) ? payload.data : [];
        setBrainstormOptions(options);
        setSelectedBrainstormId((previousId) => {
          if (previousId && options.some((option) => option.brainstormId === previousId)) {
            return previousId;
          }
          if (brainstormId && options.some((option) => option.brainstormId === brainstormId)) {
            return brainstormId;
          }
          return options[0]?.brainstormId || '';
        });
      } catch (error) {
        console.error('Failed to load brainstorm boards', error);
        setBrainstormOptions([]);
      } finally {
        setIsLoadingBrainstorms(false);
      }
    };

    loadBrainstorms();
  }, [brainstormId]);

  useEffect(() => {
    const loadBoard = async () => {
      setSelectedId(null);
      if (!effectiveBrainstormId) {
        setElements([]);
        setInvitedStaff([]);
        setBoardTitle(projectName ? `${projectName} Brainstorm` : 'Brainstorm Board');
        localVersionRef.current = 0;
        setSaveState('idle');
        skipNextAutoSaveRef.current = true;
        return;
      }

      setIsLoadingBoard(true);
      try {
        const response = await fetch(`${MONGO_BASE}/api/brainstorm/${encodeURIComponent(effectiveBrainstormId)}`, {
          method: 'GET',
          credentials: 'include',
        });

        const payload = await response.json();
        if (response.status === 403) {
          setAccessDenied(true);
          return;
        }
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.message || 'Failed to load brainstorm board');
        }

        setElements(Array.isArray(payload?.data?.elements) ? payload.data.elements : []);
        setInvitedStaff(sanitizeInvited(payload?.data?.invited));
        setBoardTitle(payload?.data?.title || projectName || 'Brainstorm Board');
        setProductOwner(payload?.data?.productOwner || null);
        localVersionRef.current = Number(payload?.data?.version) || 0;
        setSaveState('saved');
      } catch (error) {
        console.error('Failed to load brainstorm board', error);
        setElements([]);
        localVersionRef.current = 0;
        setSaveState('error');
      } finally {
        skipNextAutoSaveRef.current = true;
        setIsLoadingBoard(false);
      }
    };

    loadBoard();
  }, [effectiveBrainstormId, projectName]);

  useEffect(() => {
    if (!BRAINSTORM_REALTIME_ENABLED || !effectiveBrainstormId) {
      setRealtimeState('idle');
      setPresenceUsers([]);
      return;
    }

    let closedByEffect = false;
    const ws = new WebSocket(getBrainstormWsUrl());
    wsRef.current = ws;
    setRealtimeState('connecting');

    ws.onopen = () => {
      setRealtimeState('connected');
      ws.send(
        JSON.stringify({
          type: 'brainstorm:join',
          brainstormId: effectiveBrainstormId,
          sinceVersion: localVersionRef.current,
        })
      );
    };

    ws.onmessage = (event) => {
      let message = null;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }

      if (!message?.type) {
        return;
      }

      if (message.type === 'brainstorm:presence') {
        setPresenceUsers(Array.isArray(message.users) ? message.users : []);
        return;
      }

      if (message.type === 'brainstorm:snapshot') {
        const board = message.data || {};
        suppressNextRealtimeSyncRef.current = true;
        setElements(Array.isArray(board.elements) ? board.elements : []);
        setInvitedStaff(sanitizeInvited(board.invited));
        if (typeof board.title === 'string' && board.title.trim()) {
          setBoardTitle(board.title);
        }
        if (board.productOwner !== undefined) setProductOwner(board.productOwner || null);
        localVersionRef.current = Number(board.version) || 0;
        setSaveState('saved');
        return;
      }

      if (message.type === 'brainstorm:resync') {
        if (message.snapshot) {
          const board = message.snapshot;
          suppressNextRealtimeSyncRef.current = true;
          setElements(Array.isArray(board.elements) ? board.elements : []);
          setInvitedStaff(sanitizeInvited(board.invited));
          if (typeof board.title === 'string' && board.title.trim()) {
            setBoardTitle(board.title);
          }
          localVersionRef.current = Number(board.version) || 0;
        }

        if (Array.isArray(message.ops) && message.ops.length > 0) {
          const latest = message.ops[message.ops.length - 1];
          localVersionRef.current = Number(latest?.serverVersion) || localVersionRef.current;
        }

        setSaveState('saved');
        return;
      }

      if (message.type === 'brainstorm:ack') {
        if (message.opId) {
          sentOpIdsRef.current.delete(message.opId);
        }
        if (pendingRealtimeSaveRef.current) {
          clearTimeout(pendingRealtimeSaveRef.current);
          pendingRealtimeSaveRef.current = null;
        }
        localVersionRef.current = Number(message.serverVersion) || localVersionRef.current;
        setSaveState('saved');
        return;
      }

      if (message.type === 'brainstorm:element_engagement') {
        const { elementId, element } = message;
        if (elementId && element) {
          setElements((prev) => prev.map((e) => String(e.id) === String(elementId) ? { ...e, ...element } : e));
          setEngagementEl((prev) => prev && String(prev.id) === String(elementId) ? { ...prev, ...element } : prev);
        }
        return;
      }

      if (message.type === 'brainstorm:op') {
        const op = message.op;
        if (!op) {
          return;
        }

        localVersionRef.current = Number(op.serverVersion) || localVersionRef.current;

        if (sentOpIdsRef.current.has(op.opId)) {
          sentOpIdsRef.current.delete(op.opId);
          setSaveState('saved');
          return;
        }

        if (op.kind === 'replace_snapshot') {
          suppressNextRealtimeSyncRef.current = true;
          setElements(Array.isArray(op.payload?.elements) ? op.payload.elements : []);
          setInvitedStaff(sanitizeInvited(op.payload?.invited));
          if (typeof op.payload?.title === 'string' && op.payload.title.trim()) {
            setBoardTitle(op.payload.title);
          }
          setSaveState('saved');
        }
      }
    };

    ws.onerror = () => {
      setRealtimeState('error');
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (!closedByEffect) {
        setRealtimeState('closed');
      }
    };

    return () => {
      closedByEffect = true;
      setPresenceUsers([]);
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(
          JSON.stringify({
            type: 'brainstorm:leave',
            brainstormId: effectiveBrainstormId,
          })
        );
      }
      ws.close();
    };
  }, [effectiveBrainstormId]);

  useEffect(() => {
    if (!effectiveBrainstormId || isLoadingBoard) {
      return;
    }
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    const hasRealtimeConnection =
      BRAINSTORM_REALTIME_ENABLED &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN;

    if (suppressNextRealtimeSyncRef.current) {
      suppressNextRealtimeSyncRef.current = false;
      return;
    }

    setSaveState('saving');
    saveTimerRef.current = setTimeout(async () => {
      if (hasRealtimeConnection) {
        const opId =
          typeof window !== 'undefined' && window.crypto?.randomUUID
            ? window.crypto.randomUUID()
            : `op-${Date.now()}-${Math.random().toString(16).slice(2)}`;

        sentOpIdsRef.current.add(opId);
        try {
          wsRef.current.send(
            JSON.stringify({
              type: 'brainstorm:op',
              opId,
              baseVersion: localVersionRef.current,
              kind: 'replace_snapshot',
              payload: {
                title: boardTitle,
                elements,
                invited: invitedStaff,
                projectId,
              },
            })
          );

          if (pendingRealtimeSaveRef.current) {
            clearTimeout(pendingRealtimeSaveRef.current);
          }

          pendingRealtimeSaveRef.current = setTimeout(async () => {
            pendingRealtimeSaveRef.current = null;

            try {
              const response = await fetch(`${MONGO_BASE}/api/brainstorm/${encodeURIComponent(effectiveBrainstormId)}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                  title: boardTitle,
                  elements,
                  invited: invitedStaff,
                  projectId,
                }),
              });

              const payload = await response.json();
              if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Failed to persist brainstorm board');
              }

              setSaveState('saved');
            } catch (fallbackError) {
              console.error('Realtime save fallback failed', fallbackError);
              setSaveState('error');
            }
          }, 1500);

          setSaveState('saving');
        } catch (sendError) {
          console.error('Failed to send realtime brainstorm update', sendError);
          try {
            const response = await fetch(`${MONGO_BASE}/api/brainstorm/${encodeURIComponent(effectiveBrainstormId)}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify({
                title: boardTitle,
                elements,
                invited: invitedStaff,
                projectId,
              }),
            });

            const payload = await response.json();
            if (!response.ok || !payload?.success) {
              throw new Error(payload?.message || 'Failed to persist brainstorm board');
            }

            setSaveState('saved');
          } catch (fallbackError) {
            console.error('Realtime send and fallback save failed', fallbackError);
            setSaveState('error');
          }
        }
        return;
      }

      try {
        const response = await fetch(`${MONGO_BASE}/api/brainstorm/${encodeURIComponent(effectiveBrainstormId)}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            title: boardTitle,
            elements,
            invited: invitedStaff,
            projectId,
          }),
        });

        const payload = await response.json();
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.message || 'Failed to save brainstorm board');
        }

        setSaveState('saved');
      } catch (error) {
        console.error('Failed to save brainstorm board', error);
        setSaveState('error');
      }
    }, 700);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [effectiveBrainstormId, boardTitle, elements, invitedStaff, isLoadingBoard, projectId]);

  useEffect(() => {
    if (!inviteOpen && !todoElement) {
      return;
    }

    const loadStaff = async () => {
      setIsLoadingStaff(true);
      try {
        const response = await fetch(`${API_BASE}/api/staff`, {
          method: 'GET',
          credentials: 'include',
        });

        const payload = await response.json();
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.message || 'Failed to load staff');
        }

        const normalizedStaff = (Array.isArray(payload?.data) ? payload.data : [])
          .map((entry) => ({
            id: Number(entry?.id),
            name: (entry?.Name || entry?.name || '').toString().trim(),
            role: (entry?.Role || entry?.Department || 'Staff').toString(),
          }))
          .filter((entry) => Number.isFinite(entry.id) && entry.name);

        setStaffOptions(normalizedStaff);
      } catch (error) {
        console.error('Failed to load staff list for brainstorm invite', error);
        setStaffOptions([]);
      } finally {
        setIsLoadingStaff(false);
      }
    };

    loadStaff();
  }, [inviteOpen, todoElement]);

  // ── Drag ──────────────────────────────────────────────────────────────────
  const handleElementMouseDown = (e, id) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const el = elements.find((el) => el.id === id);
    if (!el) return;
    dragRef.current = {
      id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      origX: el.x,
      origY: el.y,
      moved: false,
    };
  };

  const handleCanvasMouseMove = useCallback((e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startMouseX;
    const dy = e.clientY - dragRef.current.startMouseY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragRef.current.moved = true;
    if (!dragRef.current.moved) return;
    const newX = snapToGrid(Math.max(0, dragRef.current.origX + dx));
    const newY = snapToGrid(Math.max(0, dragRef.current.origY + dy));
    setElements((prev) =>
      prev.map((el) =>
        el.id === dragRef.current.id ? { ...el, x: newX, y: newY } : el
      )
    );
  }, []);

  const handleCanvasMouseUp = useCallback(() => {
    if (!dragRef.current) return;
    const { id, moved } = dragRef.current;
    dragRef.current = null;
    if (!moved) {
      // treat as click → open detail editor or full-screen doc editor
      setElements((prev) => {
        const el = prev.find((e) => e.id === id);
        if (el) {
          if (el.type === 'doc') {
            setDocData({ ...el });
            setDocOpen(true);
          } else if (el.type === 'picture') {
            setPictureData({ ...el, imageUrl: el.imageUrl || '', imageSource: el.imageSource || 'url' });
            setPictureOpen(true);
          } else {
            setDetailData({ ...el });
            setDetailOpen(true);
          }
        }
        return prev;
      });
    }
  }, []);

  // ── Add element ────────────────────────────────────────────────────────────
  const openGridPicker = (type) => {
    setPendingType(type);
    setGridPickerOpen(true);
  };

  const handleGridCellClick = (col, row) => {
    const newEl = {
      id: crypto.randomUUID(),
      type: pendingType,
      x: col * GRID_SIZE,
      y: row * GRID_SIZE,
      color: activeColor,
      title: pendingType === 'sticky'
        ? 'New Note'
        : `New ${pendingType.charAt(0).toUpperCase() + pendingType.slice(1)}`,
      description: '',
      imageUrl: pendingType === 'picture' ? '' : undefined,
      imageSource: pendingType === 'picture' ? 'url' : undefined,
      number: elements.length + 1,
      createdBy: currentUserDisplayName,
      createdById: user?.id || null,
    };
    setElements((prev) => [...prev, newEl]);
    setGridPickerOpen(false);
    setPendingType(null);
    setSelectedId(newEl.id);
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = (id) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // ── Detail save ────────────────────────────────────────────────────────────
  const handleDetailSave = () => {
    if (!detailData) return;
    setElements((prev) =>
      prev.map((el) => (el.id === detailData.id ? { ...detailData } : el))
    );
    setDetailOpen(false);
    setDetailData(null);
  };

  const handleDocSave = () => {
    if (!docData) return;
    setElements((prev) =>
      prev.map((el) => (el.id === docData.id ? { ...docData } : el))
    );
    setDocOpen(false);
    setDocData(null);
    setDocPromptOpen(false);
    setDocPrompt('');
    setDocPromptError('');
  };

  const handleDocAssist = async () => {
    if (!docData || !effectiveBrainstormId) return;
    const normalizedPrompt = docPrompt.trim();
    if (!normalizedPrompt) {
      setDocPromptError('Enter a prompt first.');
      return;
    }

    setDocPromptLoading(true);
    setDocPromptError('');
    try {
      const response = await fetch(`${API_BASE}/api/brainstorm/doc-assist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          brainstormId: effectiveBrainstormId,
          elementId: docData.id,
          title: docData.title,
          description: docData.description || '',
          prompt: normalizedPrompt,
          useWebSearch: docUseWebSearch,
        }),
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to generate AI response');
      }

      const appended = String(payload?.appendedDescription || '').trim();
      const generated = String(payload?.generatedText || '').trim();
      const shouldReplace = Boolean(payload?.replaceMode);

      if (!appended && !generated) {
        throw new Error('No text was returned by AI');
      }

      setDocData((prev) => ({
        ...prev,
        description: shouldReplace
          ? (generated || appended)
          : (appended || `${prev?.description || ''}${prev?.description ? '\n\n' : ''}${generated}`),
      }));
      setDocPrompt('');
    } catch (error) {
      setDocPromptError(error?.message || 'Failed to generate AI text');
    } finally {
      setDocPromptLoading(false);
    }
  };

  const closePictureModal = () => {
    setPictureOpen(false);
    setPictureData(null);
    setPicturePrompt('');
    setPictureError('');
    setPictureUploading(false);
    setPictureGenerating(false);
    setPictureJobId('');
    setPendingGeneratedUrl('');
    setPictureSectionTab('generate');
  };

  const handlePictureSave = () => {
    if (!pictureData) return;
    setElements((prev) =>
      prev.map((el) => (el.id === pictureData.id ? { ...pictureData } : el))
    );
    closePictureModal();
  };

  const handlePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !pictureData || !effectiveBrainstormId) return;

    setPictureUploading(true);
    setPictureError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('brainstormId', effectiveBrainstormId);
      formData.append('elementId', pictureData.id);

      const response = await fetch(`${API_BASE}/api/brainstorm/picture/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const payload = await response.json();

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to upload image');
      }

      const imageUrl = resolveApiAssetUrl(payload?.imageUrl);
      setPictureData((prev) => ({
        ...prev,
        imageUrl,
        imageSource: 'upload',
      }));
    } catch (error) {
      setPictureError(error?.message || 'Failed to upload image');
    } finally {
      setPictureUploading(false);
    }
  };

  const handlePictureGenerate = async () => {
    if (!pictureData || !effectiveBrainstormId) return;
    const normalizedPrompt = picturePrompt.trim();
    if (!normalizedPrompt) {
      setPictureError('Enter an image prompt first.');
      return;
    }

    setPictureGenerating(true);
    setPictureError('');
    setPictureJobId('');

    try {
      const queueResponse = await fetch(`${API_BASE}/api/brainstorm/picture/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          brainstormId: effectiveBrainstormId,
          elementId: pictureData.id,
          prompt: normalizedPrompt,
          title: pictureData.title,
          imageReference: pictureData.imageUrl || null,
        }),
      });
      const queuePayload = await queueResponse.json();

      if (!queueResponse.ok || !queuePayload?.success || !queuePayload?.jobId) {
        throw new Error(queuePayload?.message || 'Failed to queue image generation');
      }

      const jobId = String(queuePayload.jobId);
      setPictureJobId(jobId);
      const statusPath = String(queuePayload.statusUrl || '').trim();
      const statusUrl = statusPath
        ? (statusPath.startsWith('http') ? statusPath : `${API_BASE}${statusPath}`)
        : `${API_BASE}/api/redisjobs/${encodeURIComponent(jobId)}`;

      let finalImageUrl = '';
      for (let attempt = 0; attempt < 120; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 1500));
        // eslint-disable-next-line no-await-in-loop
        const statusResponse = await fetch(statusUrl, {
          method: 'GET',
          credentials: 'include',
        });
        // eslint-disable-next-line no-await-in-loop
        const statusPayload = await statusResponse.json();

        if (!statusResponse.ok || !statusPayload?.success) {
          throw new Error(statusPayload?.message || 'Failed while checking generation status');
        }

        if (statusPayload?.status === 'failed') {
          throw new Error(statusPayload?.failedReason || 'Image generation failed');
        }

        if (statusPayload?.status === 'completed') {
          finalImageUrl = resolveApiAssetUrl(statusPayload?.result?.imageUrl);
          break;
        }
      }

      if (!finalImageUrl) {
        throw new Error('Image generation timed out');
      }

      setPendingGeneratedUrl(finalImageUrl);
      setPicturePrompt('');
    } catch (error) {
      setPictureError(error?.message || 'Failed to generate image');
    } finally {
      setPictureGenerating(false);
    }
  };

  const handlePictureDownload = () => {
    if (!pictureData?.imageUrl) return;
    const raw = String(pictureData.imageUrl);
    const separator = raw.includes('?') ? '&' : '?';
    const downloadUrl = `${raw}${separator}download=1`;
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
  };

  // ── Save / regenerate AI-generated image ──────────────────────────────────
  const handleSaveGeneratedImage = () => {
    if (!pendingGeneratedUrl) return;
    setPictureData((prev) => ({ ...prev, imageUrl: pendingGeneratedUrl, imageSource: 'generated' }));
    setPendingGeneratedUrl('');
    setPicturePrompt('');
  };

  const handleDiscardGeneratedImage = () => {
    setPendingGeneratedUrl('');
  };

  // ── Engagement helpers ─────────────────────────────────────────────────────
  const currentUserId = Number(user?.id || user?.Id || user?.staffId || 0);
  const currentUserName = currentUserDisplayName;
  const isProductOwner = !!(productOwner && Number(productOwner.id) === currentUserId);

  const handleToggleLike = async (elementId) => {
    if (!effectiveBrainstormId) return;
    setElements((prev) => prev.map((el) => {
      if (String(el.id) !== String(elementId)) return el;
      const likes = Array.isArray(el.likes) ? el.likes : [];
      const alreadyLiked = likes.some((l) => l.staffId === currentUserId);
      return { ...el, likes: alreadyLiked ? likes.filter((l) => l.staffId !== currentUserId) : [...likes, { staffId: currentUserId, staffName: currentUserName }] };
    }));
    try {
      await fetch(`${MONGO_BASE}/api/brainstorm/${encodeURIComponent(effectiveBrainstormId)}/element/${encodeURIComponent(elementId)}/like`, {
        method: 'POST', credentials: 'include',
      });
    } catch (err) { console.error('Like failed', err); }
  };

  const handleVotePoll = async (elementId, vote) => {
    if (!effectiveBrainstormId) return;
    setElements((prev) => prev.map((el) => {
      if (String(el.id) !== String(elementId)) return el;
      const yes = Array.isArray(el.poll?.yes) ? el.poll.yes : [];
      const no = Array.isArray(el.poll?.no) ? el.poll.no : [];
      const alreadyVoted = yes.some((v) => v.staffId === currentUserId) ? 'yes' : no.some((v) => v.staffId === currentUserId) ? 'no' : null;
      const newYes = yes.filter((v) => v.staffId !== currentUserId);
      const newNo = no.filter((v) => v.staffId !== currentUserId);
      if (vote === 'yes' && alreadyVoted !== 'yes') newYes.push({ staffId: currentUserId, staffName: currentUserName });
      if (vote === 'no' && alreadyVoted !== 'no') newNo.push({ staffId: currentUserId, staffName: currentUserName });
      return { ...el, poll: { yes: newYes, no: newNo } };
    }));
    try {
      const currentEl = elements.find((e) => String(e.id) === String(elementId));
      const yes = Array.isArray(currentEl?.poll?.yes) ? currentEl.poll.yes : [];
      const no = Array.isArray(currentEl?.poll?.no) ? currentEl.poll.no : [];
      const alreadyVoted = yes.some((v) => v.staffId === currentUserId) ? 'yes' : no.some((v) => v.staffId === currentUserId) ? 'no' : null;
      const sendVote = alreadyVoted === vote ? null : vote;
      await fetch(`${MONGO_BASE}/api/brainstorm/${encodeURIComponent(effectiveBrainstormId)}/element/${encodeURIComponent(elementId)}/poll`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ vote: sendVote }),
      });
    } catch (err) { console.error('Poll vote failed', err); }
  };

  const handleApproveElement = async (elementId) => {
    if (!effectiveBrainstormId || !isProductOwner) return;
    setElements((prev) => prev.map((el) => {
      if (String(el.id) !== String(elementId)) return el;
      return { ...el, approved: el.approved ? null : { by: { id: currentUserId, name: currentUserName }, at: new Date().toISOString() } };
    }));
    try {
      await fetch(`${MONGO_BASE}/api/brainstorm/${encodeURIComponent(effectiveBrainstormId)}/element/${encodeURIComponent(elementId)}/approve`, {
        method: 'POST', credentials: 'include',
      });
    } catch (err) { console.error('Approve failed', err); }
  };

  const openEngagement = (elementId) => {
    const el = elements.find((e) => String(e.id) === String(elementId));
    if (!el) return;
    setEngagementEl({ ...el });
    setNewComment('');
    setEngagementError('');
    setEngagementOpen(true);
  };

  const handleAddComment = async () => {
    const text = newComment.trim();
    if (!text || !engagementEl || !effectiveBrainstormId) return;
    setEngagementLoading(true);
    setEngagementError('');
    try {
      const response = await fetch(
        `${MONGO_BASE}/api/brainstorm/${encodeURIComponent(effectiveBrainstormId)}/element/${encodeURIComponent(engagementEl.id)}/comment`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ text }) }
      );
      const payload = await response.json();
      if (!response.ok || !payload?.success) throw new Error(payload?.message || 'Failed to add comment');
      const updatedEl = payload.element;
      setElements((prev) => prev.map((e) => String(e.id) === String(engagementEl.id) ? { ...e, ...updatedEl } : e));
      setEngagementEl((prev) => ({ ...prev, ...updatedEl }));
      setNewComment('');
    } catch (err) {
      setEngagementError(err?.message || 'Failed to add comment');
    } finally {
      setEngagementLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!engagementEl || !effectiveBrainstormId) return;
    try {
      const response = await fetch(
        `${MONGO_BASE}/api/brainstorm/${encodeURIComponent(effectiveBrainstormId)}/element/${encodeURIComponent(engagementEl.id)}/comment/${encodeURIComponent(commentId)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const payload = await response.json();
      if (!response.ok || !payload?.success) throw new Error(payload?.message || 'Failed to delete comment');
      const updatedEl = payload.element;
      setElements((prev) => prev.map((e) => String(e.id) === String(engagementEl.id) ? { ...e, ...updatedEl } : e));
      setEngagementEl((prev) => ({ ...prev, ...updatedEl }));
    } catch (err) { console.error('Delete comment failed', err); }
  };

  const handleSetProductOwner = async (staffMember) => {
    const newPO = staffMember ? { id: staffMember.id, name: staffMember.name } : null;
    setProductOwner(newPO);
    if (!effectiveBrainstormId) return;
    try {
      await fetch(`${MONGO_BASE}/api/brainstorm/${encodeURIComponent(effectiveBrainstormId)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ productOwner: newPO }),
      });
    } catch (err) { console.error('Set product owner failed', err); }
  };

  const openTodoModal = (element) => {
    setTodoElement(element);
    setTodoForm({ staffId: '', date: '', timeFrom: '', timeTo: '' });
    setTodoError('');
  };

  const closeTodoModal = () => {
    if (todoSubmitting) return;
    setTodoElement(null);
    setTodoError('');
  };

  const handleCreateTodo = async (event) => {
    event.preventDefault();
    if (!todoElement) return;

    const assignee = staffOptions.find((staff) => String(staff.id) === todoForm.staffId);
    if (!assignee) {
      setTodoError('Choose a staff member.');
      return;
    }

    setTodoSubmitting(true);
    setTodoError('');
    try {
      const response = await fetch(`${API_BASE}/api/todo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          Title: todoElement.title || 'Brainstorm action',
          Content: todoElement.description || `Created from ${boardTitle}.`,
          CalendarDate: todoForm.date,
          TimeFrom: todoForm.timeFrom,
          TimeTo: todoForm.timeTo,
          StaffId: assignee.id,
          StaffName: assignee.name,
        }),
      });
      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to create task');
      }
      setTodoElement(null);
      setTodoForm({ staffId: '', date: '', timeFrom: '', timeTo: '' });
      setTodoError('');
    } catch (error) {
      setTodoError(error?.message || 'Failed to create task');
    } finally {
      setTodoSubmitting(false);
    }
  };

  // ── New board helpers ──────────────────────────────────────────────────────
  const closeNewBoardModal = () => {
    if (newBoardCreating) return;
    setNewBoardOpen(false);
    setNewBoardMode('blank');
    setNewBoardTitle('');
    setSelectedAgent(null);
    setAgentInputs({});
    setAgentFiles({});
    setNewBoardError('');
    setAiJobId(null);
    setAiJobProgress(null);
    setAiJobPolling(false);
  };

  const handleCreateBlankBoard = async () => {
    const title = newBoardTitle.trim() || 'Untitled Brainstorm';
    setNewBoardCreating(true);
    setNewBoardError('');
    try {
      const response = await fetch(`${MONGO_BASE}/api/brainstorm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title, projectId, elements: [], invited: [] }),
      });
      const payload = await response.json();
      if (!response.ok || !payload?.success) throw new Error(payload?.message || 'Failed to create board');
      const newBoard = payload.data;
      setBrainstormOptions((prev) => [...prev, newBoard]);
      setSelectedBrainstormId(newBoard.brainstormId);
      setBoardTitle(title);
      closeNewBoardModal();
    } catch (error) {
      setNewBoardError(error?.message || 'Failed to create board');
    } finally {
      setNewBoardCreating(false);
    }
  };

  const handleAiGenerateBoard = async () => {
    if (!selectedAgent) return;
    const agent = AI_AGENTS.find((a) => a.id === selectedAgent);
    if (!agent) return;
    const missing = agent.inputs.filter(
      (f) => !f.optional && !String(agentInputs[f.key] || '').trim() && !Array.isArray(agentInputs[f.key])
    );
    if (missing.length > 0) {
      setNewBoardError(`Please fill in: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }
    const title = newBoardTitle.trim() || `${agent.title} Board`;
    setNewBoardCreating(true);
    setNewBoardError('');
    try {
      // Create blank board first to get a brainstormId
      const createRes = await fetch(`${MONGO_BASE}/api/brainstorm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title, projectId, elements: [], invited: [] }),
      });
      const createPayload = await createRes.json();
      if (!createRes.ok || !createPayload?.success) throw new Error(createPayload?.message || 'Failed to create board');
      const newBoard = createPayload.data;

      // Build FormData with agent inputs + files
      const formData = new FormData();
      formData.append('agentId', selectedAgent);
      formData.append('inputs', JSON.stringify(agentInputs));
      // Attach all files from all file fields
      for (const [, files] of Object.entries(agentFiles)) {
        const fileArray = Array.isArray(files) ? files : [files];
        for (const file of fileArray) {
          if (file instanceof File) formData.append('referenceFiles', file);
        }
      }

      const genRes = await fetch(
        `${MONGO_BASE}/api/brainstorm/${encodeURIComponent(newBoard.brainstormId)}/ai-generate`,
        { method: 'POST', credentials: 'include', body: formData }
      );
      const genPayload = await genRes.json();
      if (!genRes.ok || !genPayload?.success) throw new Error(genPayload?.message || 'Failed to start AI generation');

      // Board created — add to options immediately with the blank board
      setBrainstormOptions((prev) => [...prev, newBoard]);
      setSelectedBrainstormId(newBoard.brainstormId);
      setBoardTitle(title);

      // Start polling for job progress (keep modal open)
      setAiJobId(genPayload.jobId);
      setAiJobProgress({ percent: 1, stage: 'Queued…', status: 'waiting' });
      setAiJobPolling(true);
    } catch (error) {
      setNewBoardError(error?.message || 'Failed to generate board');
      setNewBoardCreating(false);
    }
    // Note: setNewBoardCreating(false) is NOT called here —
    // the polling useEffect handles that when the job finishes.
  };

  // ── AI job polling ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!aiJobPolling || !aiJobId) return;
    let cancelled = false;
    let timerId;

    const poll = async () => {
      try {
        const res = await fetch(
          `${MONGO_BASE}/api/brainstorm/ai-generate/status/${encodeURIComponent(aiJobId)}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error('Status check failed');
        const payload = await res.json();
        const jobData = payload?.data || {};
        if (cancelled) return;
        const status = jobData.status;
        setAiJobProgress({
          percent: jobData.progress?.percent ?? 0,
          stage: jobData.progress?.stage || 'Processing…',
          status,
        });
        if (status === 'completed') {
          setAiJobPolling(false);
          setNewBoardCreating(false);
          // Reload the board to get the AI-generated elements and display them
          const completedBrainstormId = jobData.result?.brainstormId;
          if (completedBrainstormId) {
            try {
              const boardRes = await fetch(
                `${MONGO_BASE}/api/brainstorm/${encodeURIComponent(completedBrainstormId)}`,
                { credentials: 'include' }
              );
              const boardPayload = await boardRes.json();
              if (boardPayload?.success && boardPayload?.data) {
                const boardData = boardPayload.data;
                // Update the options list with the full board data
                setBrainstormOptions((prev) =>
                  prev.map((b) => (b.brainstormId === completedBrainstormId ? boardData : b))
                );
                // Push the AI-generated elements directly into canvas state
                setElements(Array.isArray(boardData.elements) ? boardData.elements : []);
                setBoardTitle(boardData.title || 'Brainstorm Board');
                localVersionRef.current = Number(boardData.version) || 0;
                skipNextAutoSaveRef.current = true;
                setSaveState('saved');
              }
            } catch {
              // non-fatal – board is already selected, user can refresh
            }
          }
          // Close modal after a brief moment so user sees 100%
          setTimeout(() => {
            if (!cancelled) closeNewBoardModal();
          }, 1200);
          return;
        }
        if (status === 'failed') {
          setAiJobPolling(false);
          setNewBoardCreating(false);
          setNewBoardError(jobData.failedReason || 'AI generation failed');
          return;
        }
      } catch {
        // silently retry
      }
      if (!cancelled) timerId = setTimeout(poll, 2500);
    };

    timerId = setTimeout(poll, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [aiJobPolling, aiJobId]);

  // ── Invite helpers ─────────────────────────────────────────────────────────
  const toggleStaff = (staff) =>
    setInvitedStaff((prev) =>
      prev.some((entry) => entry.id === staff.id)
        ? prev.filter((entry) => entry.id !== staff.id)
        : [...prev, { id: staff.id, name: staff.name }]
    );

  const handleInviteDone = async () => {
    if (!effectiveBrainstormId || invitedStaff.length === 0) {
      setInviteOpen(false);
      return;
    }

    setInviteSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/brainstorm/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          brainstormId: effectiveBrainstormId,
          boardTitle,
          projectId,
          invited: invitedStaff,
        }),
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to send brainstorm invites');
      }
    } catch (error) {
      console.error('Failed to send brainstorm invites', error);
    } finally {
      setInviteSubmitting(false);
      setInviteOpen(false);
    }
  };

  const handleCopyLink = () => {
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const getElementMetrics = (el) => {
    if (el.type === 'sticky') return { width: 160, height: 160 };
    if (el.type === 'rectangle') return { width: 180, height: 140 };
    if (el.type === 'circle') return { width: 130, height: 130 };
    if (el.type === 'diamond') return { width: 130, height: 130 };
    if (el.type === 'doc') return { width: 180, height: 150 };
    if (el.type === 'picture') return { width: 220, height: 180 };
    return { width: 140, height: 140 };
  };

  const sortedByNumber = [...elements]
    .filter((el) => Number.isFinite(Number(el.number)))
    .sort((a, b) => Number(a.number) - Number(b.number));

  const connectorPairs = sortedByNumber
    .map((to, index) => ({ from: sortedByNumber[index - 1], to }))
    .filter((pair) => pair.from && pair.to);

  const renderConnectors = () => (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={CANVAS_WIDTH}
      height={CANVAS_HEIGHT}
      viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
      aria-hidden="true"
    >
      <defs>
        <marker id="brainstorm-arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
        </marker>
      </defs>
      {connectorPairs.map((pair, index) => {
        const fromSize = getElementMetrics(pair.from);
        const toSize = getElementMetrics(pair.to);
        const x1 = pair.from.x + fromSize.width / 2;
        const y1 = pair.from.y + fromSize.height / 2;
        const x2 = pair.to.x + toSize.width / 2;
        const y2 = pair.to.y + toSize.height / 2;

        return (
          <g key={`${pair.from.id}-${pair.to.id}-${index}`}>
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#64748b"
              strokeWidth="2"
              strokeDasharray="6 6"
              markerEnd="url(#brainstorm-arrow)"
            />
          </g>
        );
      })}
    </svg>
  );

  // ── Element renderer ───────────────────────────────────────────────────────
  const renderElement = (el) => {
    const isSelected = selectedId === el.id;
    const { bg, border, text } = el.color;
    const selectionGlow = `drop-shadow(0 0 8px ${border})`;

    const NumberBadge = () => (
      <span
        className="inline-flex items-center justify-center h-5 w-5 rounded-full text-xs font-bold text-white shrink-0"
        style={{ background: border }}
      >
        {el.number}
      </span>
    );

    const CreatedBy = ({ cls = '' }) => (
      <span className={`text-xs opacity-60 ${cls}`} style={{ color: text }}>
        {el.createdBy}
      </span>
    );

    const elLikes = Array.isArray(el.likes) ? el.likes : [];
    const elComments = Array.isArray(el.comments) ? el.comments : [];
    const elPollYes = Array.isArray(el.poll?.yes) ? el.poll.yes : [];
    const elPollNo = Array.isArray(el.poll?.no) ? el.poll.no : [];
    const hasLiked = elLikes.some((l) => l.staffId === currentUserId);
    const hasVotedYes = elPollYes.some((v) => v.staffId === currentUserId);
    const hasVotedNo = elPollNo.some((v) => v.staffId === currentUserId);

    const engagementBar = (
      <div
        className="flex items-center gap-1.5 mt-auto pt-1 border-t border-black/10"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          className={`flex items-center gap-0.5 text-xs transition-opacity ${hasLiked ? 'text-rose-500' : 'opacity-40 hover:opacity-70'}`}
          onClick={() => handleToggleLike(el.id)}
          title="Like"
        >
          <HeartIcon className="h-3 w-3" />
          <span>{elLikes.length || ''}</span>
        </button>
        <button
          className="flex items-center gap-0.5 text-xs opacity-40 hover:opacity-70 transition-opacity"
          onClick={() => openEngagement(el.id)}
          title="Comments"
        >
          <ChatBubbleLeftEllipsisIcon className="h-3 w-3" />
          <span>{elComments.length || ''}</span>
        </button>
        <button
          className={`text-xs font-medium transition-opacity ${hasVotedYes ? 'text-emerald-600' : 'opacity-40 hover:opacity-70'}`}
          onClick={() => handleVotePoll(el.id, 'yes')}
          title="Vote Yes"
        >
          ✓{elPollYes.length > 0 ? elPollYes.length : ''}
        </button>
        <button
          className={`text-xs font-medium transition-opacity ${hasVotedNo ? 'text-rose-600' : 'opacity-40 hover:opacity-70'}`}
          onClick={() => handleVotePoll(el.id, 'no')}
          title="Vote No"
        >
          ✗{elPollNo.length > 0 ? elPollNo.length : ''}
        </button>
        {el.approved && !isProductOwner && (
          <span className="ml-auto text-sm" title={`Approved by ${el.approved?.by?.name || 'product owner'}`}>✅</span>
        )}
        {isProductOwner && (
          <button
            className="ml-auto text-sm transition-opacity hover:opacity-80"
            onClick={() => handleApproveElement(el.id)}
            title={el.approved ? 'Unapprove' : 'Approve'}
          >
            {el.approved ? '✅' : <span className="text-xs opacity-50">○ approve</span>}
          </button>
        )}
      </div>
    );

    let inner;
    let wrapperStyle;

    // ── Sticky ──
    if (el.type === 'sticky') {
      const tilt = [-2, 1, -1][el.number % 3];
      wrapperStyle = {
        left: el.x, top: el.y, width: 160, minHeight: 160,
        transform: `rotate(${tilt}deg)`,
        filter: isSelected ? selectionGlow : 'drop-shadow(2px 4px 8px rgba(0,0,0,0.18))',
        zIndex: isSelected ? 50 : 10,
      };
      inner = (
        <div
          style={{ background: bg, borderBottom: `4px solid ${border}` }}
          className="w-full h-full rounded-sm p-3 flex flex-col gap-1 min-h-[160px]"
        >
          <div className="flex items-start justify-between gap-1">
            <NumberBadge />
            <CreatedBy />
          </div>
          <p className="text-sm font-semibold mt-1 leading-tight break-words" style={{ color: text }}>
            {el.title}
          </p>
          {el.description && (
            <p className="text-xs opacity-80 mt-1 leading-snug break-words line-clamp-4" style={{ color: text }}>
              {el.description}
            </p>
          )}
          {engagementBar}
        </div>
      );

    // ── Rectangle ──
    } else if (el.type === 'rectangle') {
      wrapperStyle = {
        left: el.x, top: el.y, width: 180, minHeight: 140,
        filter: isSelected ? selectionGlow : 'drop-shadow(2px 4px 6px rgba(0,0,0,0.12))',
        zIndex: isSelected ? 50 : 10,
      };
      inner = (
        <div
          style={{ background: bg, border: `2px solid ${border}` }}
          className="w-full h-full rounded-xl p-3 flex flex-col gap-1 min-h-[140px]"
        >
          <div className="flex items-start justify-between gap-1">
            <NumberBadge />
            <CreatedBy />
          </div>
          <p className="text-sm font-semibold mt-1 leading-tight break-words" style={{ color: text }}>
            {el.title}
          </p>
          {el.description && (
            <p className="text-xs opacity-75 mt-1 leading-snug break-words line-clamp-3" style={{ color: text }}>
              {el.description}
            </p>
          )}
          {engagementBar}
        </div>
      );

    // ── Circle ──
    } else if (el.type === 'circle') {
      wrapperStyle = {
        left: el.x, top: el.y, width: 130, height: 130,
        filter: isSelected ? selectionGlow : 'drop-shadow(2px 4px 6px rgba(0,0,0,0.12))',
        zIndex: isSelected ? 50 : 10,
      };
      inner = (
        <div
          style={{ background: bg, border: `3px solid ${border}`, borderRadius: '50%' }}
          className="w-full h-full flex flex-col items-center justify-center p-3 text-center"
        >
          <NumberBadge />
          <p className="text-xs font-semibold mt-1.5 leading-tight break-words px-1" style={{ color: text }}>
            {el.title}
          </p>
        </div>
      );

    // ── Diamond ──
    } else if (el.type === 'diamond') {
      wrapperStyle = {
        left: el.x, top: el.y, width: 130, height: 130,
        filter: isSelected ? selectionGlow : 'drop-shadow(2px 4px 6px rgba(0,0,0,0.1))',
        zIndex: isSelected ? 50 : 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      };
      inner = (
        <div
          style={{
            background: bg,
            border: `2px solid ${border}`,
            transform: 'rotate(45deg)',
            width: 90, height: 90,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div style={{ transform: 'rotate(-45deg)', textAlign: 'center', padding: '4px' }}>
            <div className="flex justify-center mb-1">
              <NumberBadge />
            </div>
            <p className="text-xs font-semibold leading-tight" style={{ color: text, maxWidth: 60 }}>
              {el.title}
            </p>
          </div>
        </div>
      );

    // ── Doc ──
    } else if (el.type === 'doc') {
      wrapperStyle = {
        left: el.x,
        top: el.y,
        width: 180,
        minHeight: 150,
        filter: isSelected ? selectionGlow : 'drop-shadow(2px 4px 8px rgba(0,0,0,0.12))',
        zIndex: isSelected ? 50 : 10,
      };
      inner = (
        <div
          style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', border: `2px solid ${border}` }}
          className="w-full h-full rounded-xl p-3 flex flex-col gap-2 min-h-[150px]"
        >
          <div className="flex items-start justify-between gap-1">
            <NumberBadge />
            <CreatedBy />
          </div>
          <div className="flex items-center gap-2 text-slate-500" aria-hidden="true">
            <div className="h-6 w-5 rounded-sm border border-slate-300 bg-white flex flex-col justify-center gap-0.5 px-1">
              <span className="block h-px w-full bg-slate-300" />
              <span className="block h-px w-4/5 bg-slate-300" />
              <span className="block h-px w-3/5 bg-slate-300" />
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-wide">Doc</span>
          </div>
          <p className="text-sm font-semibold leading-tight break-words" style={{ color: text }}>
            {el.title}
          </p>
          {el.description && (
            <p className="text-xs opacity-75 leading-snug break-words line-clamp-4" style={{ color: text }}>
              {el.description}
            </p>
          )}
          {!el.description && (
            <p className="text-xs opacity-50 italic" style={{ color: text }}>
              Click to open the full document editor
            </p>
          )}
          {engagementBar}
        </div>
      );

    // ── Picture ──
    } else if (el.type === 'picture') {
      wrapperStyle = {
        left: el.x,
        top: el.y,
        width: 220,
        minHeight: 180,
        filter: isSelected ? selectionGlow : 'drop-shadow(2px 4px 8px rgba(0,0,0,0.12))',
        zIndex: isSelected ? 50 : 10,
      };

      const imageUrl = resolveApiAssetUrl(el.imageUrl);

      inner = (
        <div
          style={{ background: bg, border: `2px solid ${border}` }}
          className="w-full h-full rounded-xl p-2.5 flex flex-col gap-2 min-h-[180px]"
        >
          <div className="flex items-start justify-between gap-1">
            <NumberBadge />
            <CreatedBy />
          </div>
          <div className="w-full h-24 rounded-lg overflow-hidden border border-slate-200 bg-white/80 flex items-center justify-center">
            {imageUrl ? (
              <img src={imageUrl} alt={el.title || 'Brainstorm picture'} className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <PhotoIcon className="h-4 w-4" />
                <span>No image yet</span>
              </div>
            )}
          </div>
          <p className="text-sm font-semibold leading-tight break-words" style={{ color: text }}>
            {el.title}
          </p>
          {engagementBar}
        </div>
      );
    }

    return (
      <div
        key={el.id}
        className="absolute select-none cursor-grab active:cursor-grabbing"
        style={wrapperStyle}
        onMouseDown={(e) => handleElementMouseDown(e, el.id)}
        onClick={(e) => { e.stopPropagation(); setSelectedId(el.id); }}
      >
        {inner}
        {isSelected && (
          <div className="absolute -top-3 -right-3 z-50 flex items-center gap-1">
            <button
              className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-white shadow-md transition hover:bg-indigo-700"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); openTodoModal(el); }}
              title="Create scheduled task"
            >
              <CalendarDaysIcon className="h-3.5 w-3.5" />
            </button>
            <button
              className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow-md transition hover:bg-red-600"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); handleDelete(el.id); }}
              title="Delete"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const selectedEl = elements.find((e) => e.id === selectedId);

  const openElementEditor = (element) => {
    if (!element) return;
    setSelectedId(element.id);
    if (element.type === 'doc') {
      setDocData({ ...element });
      setDocOpen(true);
    } else if (element.type === 'picture') {
      setPictureData({
        ...element,
        imageUrl: element.imageUrl || '',
        imageSource: element.imageSource || 'url',
      });
      setPictureOpen(true);
    } else {
      setDetailData({ ...element });
      setDetailOpen(true);
    }
  };

  const tableElements = [...elements].sort((first, second) => {
    const firstNumber = Number(first.number);
    const secondNumber = Number(second.number);
    if (Number.isFinite(firstNumber) && Number.isFinite(secondNumber)) {
      return firstNumber - secondNumber;
    }
    return String(first.title || '').localeCompare(String(second.title || ''));
  });

  // ── UI ─────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col rounded-xl overflow-hidden border border-indigo-100 shadow-sm" style={{ minHeight: 0 }}>

      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2 sm:py-3 bg-white/95 backdrop-blur-md border-b border-indigo-100 sticky top-0 z-30">

        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 transition"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Back
        </button>

        {/* Brand */}
        <div className="flex items-center gap-2 mr-1">
          <SparklesIcon className="h-5 w-5 text-indigo-500" />
          <input
            type="text"
            value={boardTitle}
            onChange={(e) => setBoardTitle(e.target.value)}
            placeholder={projectName ? `${projectName} Brainstorm` : 'Brainstorm'}
            className="hidden sm:block w-44 rounded-md border border-indigo-200 bg-white/90 px-2 py-1 text-sm font-semibold text-indigo-900 outline-none focus:border-indigo-400"
          />
        </div>

        <div className="flex items-center gap-1.5 min-w-0 flex-1 sm:flex-none sm:min-w-[180px] sm:max-w-[260px]">
          <span className="text-xs font-semibold text-indigo-700 shrink-0">Board</span>
          <select
            value={effectiveBrainstormId}
            onChange={(e) => setSelectedBrainstormId(e.target.value)}
            className="w-full min-w-0 rounded-md border border-indigo-200 bg-white px-2 py-1 text-xs text-indigo-900 outline-none focus:border-indigo-400"
          >
            {!effectiveBrainstormId && (
              <option value="">{isLoadingBrainstorms ? 'Loading boards...' : 'Select board'}</option>
            )}
            {brainstormOptions.map((board) => (
              <option key={board.brainstormId} value={board.brainstormId}>
                {board.title || 'Untitled Brainstorm'}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setNewBoardOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 transition shrink-0"
          title="Create a new brainstorm board"
        >
          <PlusIcon className="h-4 w-4" />
          <span className="hidden sm:inline">New Board</span>
        </button>
        <div className="inline-flex h-8 items-center rounded-lg border border-indigo-200 bg-indigo-50 p-0.5" aria-label="Brainstorm view">
          <button
            type="button"
            onClick={() => setViewMode('canvas')}
            className={`inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-semibold transition ${viewMode === 'canvas' ? 'bg-white text-indigo-800 shadow-sm' : 'text-indigo-500 hover:text-indigo-700'}`}
            title="Canvas view"
          >
            <Squares2X2Icon className="h-4 w-4" />
            <span className="hidden lg:inline">Canvas</span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode('table')}
            className={`inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-semibold transition ${viewMode === 'table' ? 'bg-white text-indigo-800 shadow-sm' : 'text-indigo-500 hover:text-indigo-700'}`}
            title="Table view"
          >
            <TableCellsIcon className="h-4 w-4" />
            <span className="hidden lg:inline">Table</span>
          </button>
        </div>
        <div className="hidden sm:block h-5 w-px bg-indigo-200" />

        {/* Add shapes — scrollable on mobile */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-none -mx-1 px-1 py-0.5 w-full sm:w-auto sm:flex-none">
          <ToolbarBtn onClick={() => openGridPicker('sticky')}  colors="bg-yellow-50 border-yellow-300 text-yellow-800 hover:bg-yellow-100" icon="🗒"  label="Sticky" />
          <ToolbarBtn onClick={() => openGridPicker('rectangle')} colors="bg-sky-50 border-sky-300 text-sky-800 hover:bg-sky-100" icon="▭"  label="Rect" />
          <ToolbarBtn onClick={() => openGridPicker('circle')} colors="bg-green-50 border-green-300 text-green-800 hover:bg-green-100" icon="◯"  label="Circle" />
          <ToolbarBtn onClick={() => openGridPicker('diamond')} colors="bg-purple-50 border-purple-300 text-purple-800 hover:bg-purple-100" icon="◇"  label="Diamond" />
          <ToolbarBtn onClick={() => openGridPicker('doc')} colors="bg-slate-50 border-slate-300 text-slate-800 hover:bg-slate-100" icon="📄" label="Doc" />
          <ToolbarBtn onClick={() => openGridPicker('picture')} colors="bg-rose-50 border-rose-300 text-rose-800 hover:bg-rose-100" icon="🖼" label="Pic" />
        </div>

        <div className="hidden sm:block h-5 w-px bg-indigo-200" />

        {/* Color palette */}
        <div className="flex items-center gap-1.5">
          {PRESET_COLORS.map((c) => (
            <button
              key={c.label}
              title={c.label}
              onClick={() => setActiveColor(c)}
              className="h-6 w-6 rounded-full border-2 transition-transform hover:scale-110"
              style={{
                background: c.bg,
                borderColor: activeColor.label === c.label ? c.border : '#e2e8f0',
                boxShadow: activeColor.label === c.label ? `0 0 0 2px ${c.border}` : 'none',
              }}
            />
          ))}
          <span className="text-xs text-gray-400 ml-1 hidden sm:inline">active colour</span>
        </div>

        {/* Selected element actions */}
        {selectedEl && (
          <>
            <div className="h-5 w-px bg-indigo-200" />
            <button
              onClick={() => openTodoModal(selectedEl)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition"
            >
              <CalendarDaysIcon className="h-3.5 w-3.5" /> Create task
            </button>
            <button
              onClick={() => openElementEditor(selectedEl)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 transition"
            >
              <PencilIcon className="h-3.5 w-3.5" /> Edit
            </button>
            <button
              onClick={() => handleDelete(selectedId)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-100 transition"
            >
              <TrashIcon className="h-3.5 w-3.5" /> Delete
            </button>
          </>
        )}

        {/* Right side */}
        <div className="ml-auto flex items-center gap-3">
          <span className={`text-xs hidden sm:block ${saveState === 'error' ? 'text-red-500' : 'text-gray-400'}`}>
            {isLoadingBoard
              ? 'Loading...'
              : saveState === 'saving'
              ? 'Saving...'
              : saveState === 'saved'
              ? 'Saved'
              : saveState === 'error'
              ? 'Save failed'
              : 'Not saved'}
          </span>
          <span className={`text-xs hidden sm:block ${realtimeState === 'connected' ? 'text-green-600' : 'text-gray-400'}`}>
            {realtimeState === 'connected'
              ? `Realtime on${presenceUsers.length ? ` (${presenceUsers.length})` : ''}`
              : realtimeState === 'connecting'
              ? 'Realtime connecting...'
              : realtimeState === 'error'
              ? 'Realtime error'
              : ''}
          </span>
          <span className="text-xs text-gray-400 hidden sm:block">
            {elements.length} element{elements.length !== 1 ? 's' : ''}
          </span>
          {productOwner && (
            <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 shrink-0">
              <StarIcon className="h-3.5 w-3.5 text-amber-500" />
              <span className="hidden sm:inline">PO: {productOwner.name}</span>
            </div>
          )}
          <button
            onClick={() => setInviteOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-indigo-700 to-sky-500 text-white text-xs font-semibold hover:from-indigo-800 hover:to-sky-600 transition shadow-sm shrink-0"
          >
            <UserPlusIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Invite Staff</span>
          </button>
        </div>
      </div>

      {/* ── Access Denied ── */}
      {accessDenied && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-8">
            <p className="text-4xl mb-3">🔒</p>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Access Denied</h2>
            <p className="text-sm text-gray-500">You have not been invited to this brainstorm board.</p>
          </div>
        </div>
      )}

      {!accessDenied && viewMode === 'table' && (
        <div className="flex-1 overflow-auto bg-slate-50 p-3 sm:p-6">
          <div className="mx-auto w-full max-w-7xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
              <div>
                <h2 className="text-sm font-bold text-slate-900">{boardTitle}</h2>
                <p className="text-xs text-slate-500">{tableElements.length} board element{tableElements.length === 1 ? '' : 's'}</p>
              </div>
              <span className="text-xs font-medium text-slate-500">Ordered by board number</span>
            </div>

            {tableElements.length === 0 ? (
              <div className="px-5 py-16 text-center">
                <TableCellsIcon className="mx-auto h-9 w-9 text-slate-300" />
                <p className="mt-3 text-sm font-semibold text-slate-700">No elements to display</p>
                <p className="mt-1 text-xs text-slate-500">Switch to Canvas to add the first element.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-[880px] w-full divide-y divide-slate-200 text-left">
                  <thead className="bg-slate-100">
                    <tr className="text-xs font-bold uppercase text-slate-600">
                      <th className="w-16 px-4 py-3">No.</th>
                      <th className="px-4 py-3">Element</th>
                      <th className="w-32 px-4 py-3">Type</th>
                      <th className="w-40 px-4 py-3">Created by</th>
                      <th className="w-36 px-4 py-3">Engagement</th>
                      <th className="w-28 px-4 py-3">Status</th>
                      <th className="w-36 px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {tableElements.map((element) => {
                      const likes = Array.isArray(element.likes) ? element.likes.length : 0;
                      const comments = Array.isArray(element.comments) ? element.comments.length : 0;
                      const votes = (Array.isArray(element.poll?.yes) ? element.poll.yes.length : 0) +
                        (Array.isArray(element.poll?.no) ? element.poll.no.length : 0);
                      return (
                        <tr key={element.id} className="hover:bg-indigo-50/40">
                          <td className="px-4 py-3">
                            <span
                              className="inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-bold text-white"
                              style={{ backgroundColor: element.color?.border || '#64748b' }}
                            >
                              {element.number || '–'}
                            </span>
                          </td>
                          <td className="max-w-md px-4 py-3">
                            <button type="button" onClick={() => openElementEditor(element)} className="block max-w-full text-left">
                              <span className="block truncate text-sm font-bold text-slate-900 hover:text-indigo-700">{element.title || 'Untitled element'}</span>
                              <span className="mt-0.5 block max-w-md truncate text-xs text-slate-500">{element.description || 'No description'}</span>
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold capitalize text-slate-700">
                              {element.type || 'element'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{element.createdBy || 'Unknown'}</td>
                          <td className="px-4 py-3">
                            <button type="button" onClick={() => openEngagement(element.id)} className="inline-flex items-center gap-3 text-xs font-semibold text-slate-600 hover:text-indigo-700" title="Open engagement">
                              <span className="inline-flex items-center gap-1"><HeartIcon className="h-4 w-4" />{likes}</span>
                              <span className="inline-flex items-center gap-1"><ChatBubbleLeftEllipsisIcon className="h-4 w-4" />{comments}</span>
                              <span>{votes} votes</span>
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            {element.approved ? (
                              <span className="inline-flex rounded-md bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-800">Approved</span>
                            ) : (
                              <span className="inline-flex rounded-md bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">Review</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {isProductOwner && (
                                <button type="button" onClick={() => handleApproveElement(element.id)} className="rounded-md p-2 text-amber-600 hover:bg-amber-100" title={element.approved ? 'Remove approval' : 'Approve element'}>
                                  <StarIcon className="h-4 w-4" />
                                </button>
                              )}
                              <button type="button" onClick={() => openElementEditor(element)} className="rounded-md p-2 text-indigo-600 hover:bg-indigo-100" title="Edit element">
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={() => openTodoModal(element)} className="rounded-md p-2 text-emerald-600 hover:bg-emerald-100" title="Create scheduled task">
                                <CalendarDaysIcon className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={() => handleDelete(element.id)} className="rounded-md p-2 text-rose-600 hover:bg-rose-100" title="Delete element">
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Canvas ── */}
      {!accessDenied && viewMode === 'canvas' && <div
        className="relative flex-1 overflow-auto"
        style={{ background: '#f8fafc', cursor: dragRef.current ? 'grabbing' : 'default', minHeight: 280 }}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onMouseLeave={handleCanvasMouseUp}
        onClick={() => setSelectedId(null)}
      >
        {/* Empty state hint */}
        {elements.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="text-center opacity-40">
              <SparklesIcon className="h-10 w-10 text-indigo-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-indigo-400">Add a sticky note or shape to get started</p>
              <p className="text-xs text-gray-400 mt-1">Use the toolbar above to place elements on the board</p>
            </div>
          </div>
        )}

        <div
          ref={canvasRef}
          style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT, position: 'relative', ...DOT_BG }}
        >
          {renderConnectors()}
          {elements.map(renderElement)}
        </div>
      </div>}

      {/* ══════════════════════════════════════════════════════════════════
          GRID PICKER MODAL
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={gridPickerOpen}
        onClose={() => { setGridPickerOpen(false); setPendingType(null); }}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-indigo-100 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-900 to-sky-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-semibold">Choose placement</h2>
                <p className="text-indigo-200 text-xs mt-0.5 capitalize">
                  Click a cell to place your {pendingType}
                </p>
              </div>
              <button
                onClick={() => { setGridPickerOpen(false); setPendingType(null); }}
                className="text-white/70 hover:text-white transition"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Grid */}
            <div className="p-6">
              <div
                className="grid gap-1.5 mx-auto"
                style={{ gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))` }}
              >
                {Array.from({ length: GRID_ROWS }).flatMap((_, row) =>
                  Array.from({ length: GRID_COLS }).map((_, col) => {
                    const isHover = hoverCell?.col === col && hoverCell?.row === row;
                    return (
                      <button
                        key={`${col}-${row}`}
                        className="rounded transition-all"
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          background: isHover ? '#6366f1' : '#f1f5f9',
                          border: `1px solid ${isHover ? '#4338ca' : '#e2e8f0'}`,
                          transform: isHover ? 'scale(1.15)' : 'scale(1)',
                        }}
                        onMouseEnter={() => setHoverCell({ col, row })}
                        onMouseLeave={() => setHoverCell(null)}
                        onClick={() => handleGridCellClick(col, row)}
                        title={`Column ${col + 1}, Row ${row + 1}`}
                      />
                    );
                  })
                )}
              </div>
              <p className="text-xs text-gray-400 text-center mt-3">
                {hoverCell
                  ? `Position → Column ${hoverCell.col + 1}, Row ${hoverCell.row + 1}`
                  : 'Hover a cell to preview placement'}
              </p>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <Dialog open={Boolean(todoElement)} onClose={closeTodoModal} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-4">
          <DialogPanel className="w-full max-w-md overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-2xl">
            <div className="flex items-center justify-between bg-gradient-to-r from-indigo-900 to-sky-600 px-5 py-4">
              <div>
                <h2 className="text-sm font-semibold text-white">Schedule task</h2>
                <p className="mt-0.5 truncate text-xs text-indigo-200">{todoElement?.title || 'Brainstorm action'}</p>
              </div>
              <button type="button" onClick={closeTodoModal} disabled={todoSubmitting} className="text-white/70 transition hover:text-white disabled:opacity-40" aria-label="Close task scheduler">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateTodo} className="space-y-4 p-5">
              <label className="block text-sm font-semibold text-gray-800">Assign to
                <select required value={todoForm.staffId} onChange={(event) => setTodoForm((previous) => ({ ...previous, staffId: event.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500">
                  <option value="">Select staff member</option>
                  {staffOptions.map((staff) => <option key={staff.id} value={staff.id}>{staff.name}{staff.role ? ` - ${staff.role}` : ''}</option>)}
                </select>
              </label>
              <label className="block text-sm font-semibold text-gray-800">Date
                <input required type="date" value={todoForm.date} onChange={(event) => setTodoForm((previous) => ({ ...previous, date: event.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm font-semibold text-gray-800">From
                  <input required type="time" value={todoForm.timeFrom} onChange={(event) => setTodoForm((previous) => ({ ...previous, timeFrom: event.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500" />
                </label>
                <label className="block text-sm font-semibold text-gray-800">To
                  <input required type="time" value={todoForm.timeTo} onChange={(event) => setTodoForm((previous) => ({ ...previous, timeTo: event.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500" />
                </label>
              </div>
              {isLoadingStaff && <p className="text-xs text-gray-500">Loading staff...</p>}
              {todoError && <p className="text-xs font-medium text-rose-600">{todoError}</p>}
              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button type="button" onClick={closeTodoModal} disabled={todoSubmitting} className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                <button disabled={todoSubmitting || isLoadingStaff} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"><CalendarDaysIcon className="h-4 w-4" />{todoSubmitting ? 'Creating...' : 'Create task'}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          DOC FULL-SCREEN MODAL
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={docOpen}
        onClose={() => {
          setDocOpen(false);
          setDocData(null);
          setDocPromptOpen(false);
          setDocPrompt('');
          setDocPromptError('');
        }}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
        <div className="fixed inset-0 p-0 sm:p-4">
          <DialogPanel className="h-full w-full bg-white sm:rounded-3xl sm:shadow-2xl overflow-hidden border border-slate-200 flex flex-col">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-slate-700">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Document</p>
                <h2 className="text-white font-semibold truncate">
                  {docData?.title || 'Untitled Doc'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setDocOpen(false);
                  setDocData(null);
                  setDocPromptOpen(false);
                  setDocPrompt('');
                  setDocPromptError('');
                }}
                className="text-white/70 hover:text-white transition rounded-full p-2 hover:bg-white/10"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-auto bg-slate-50">
              <div className="mx-auto w-full max-w-5xl px-4 sm:px-8 py-6 sm:py-10">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-8 space-y-6 min-h-[70vh]">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Title</label>
                    <input
                      type="text"
                      value={docData?.title || ''}
                      onChange={(e) => setDocData((d) => ({ ...d, title: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      placeholder="Document title"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-semibold text-slate-900">Description</label>
                    <p className="text-xs text-slate-500">Use this area for longer text. The canvas card stays compact, but the editor is full screen.</p>
                    <textarea
                      rows={22}
                      value={docData?.description || ''}
                      onChange={(e) => setDocData((d) => ({ ...d, description: e.target.value }))}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-4 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none leading-6"
                      placeholder="Write the long-form document here..."
                    />
                  </div>

                  {/* AI Writing Assistant */}
                  <div className="rounded-2xl overflow-hidden border border-indigo-200/60 shadow-sm">
                    <button
                      onClick={() => setDocPromptOpen((prev) => !prev)}
                      className="w-full flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-indigo-950 via-indigo-800 to-violet-700 hover:from-indigo-900 hover:to-violet-600 transition-all group"
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="flex items-center justify-center h-7 w-7 rounded-lg bg-white/10 ring-1 ring-white/20 group-hover:bg-white/20 transition">
                          <SparklesIcon className="h-4 w-4 text-indigo-200" />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-white leading-none">AI Writing Assistant</span>
                          <span className="block text-xs text-indigo-300 mt-0.5">Extend, rewrite or summarise with AI</span>
                        </span>
                      </span>
                      <svg
                        className={`h-4 w-4 text-indigo-300 transition-transform ${docPromptOpen ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {docPromptOpen && (
                      <div className="bg-gradient-to-br from-indigo-950/95 to-slate-900 px-5 pb-5 pt-4 space-y-4">
                        {/* Prompt textarea */}
                        <div className="relative">
                          <textarea
                            rows={4}
                            value={docPrompt}
                            onChange={(e) => setDocPrompt(e.target.value)}
                            className="w-full rounded-xl border border-indigo-500/40 bg-white/5 px-4 py-3 text-sm text-indigo-50 placeholder:text-indigo-400/60 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none resize-none leading-relaxed"
                            placeholder="e.g. &quot;Add three bullet points about market risks&quot; or &quot;Rewrite in a formal tone&quot;…"
                          />
                          <SparklesIcon className="absolute top-3 right-3 h-4 w-4 text-indigo-400/40 pointer-events-none" />
                        </div>

                        {/* Controls */}
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          {/* Web search toggle pill */}
                          <button
                            type="button"
                            onClick={() => setDocUseWebSearch((v) => !v)}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                              docUseWebSearch
                                ? 'bg-sky-500/20 border-sky-400/60 text-sky-300 hover:bg-sky-500/30'
                                : 'bg-white/5 border-white/10 text-indigo-300 hover:bg-white/10'
                            }`}
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                            </svg>
                            {docUseWebSearch ? 'Web search: ON' : 'Web search: OFF'}
                          </button>

                          {/* Generate button */}
                          <button
                            onClick={handleDocAssist}
                            disabled={docPromptLoading}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white text-xs font-bold shadow-lg shadow-indigo-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                          >
                            {docPromptLoading ? (
                              <>
                                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                </svg>
                                Generating…
                              </>
                            ) : (
                              <>
                                <SparklesIcon className="h-4 w-4" />
                                Generate Text
                              </>
                            )}
                          </button>
                        </div>

                        {docPromptError && (
                          <div className="flex items-center gap-2 rounded-lg bg-rose-500/15 border border-rose-400/30 px-3 py-2">
                            <svg className="h-4 w-4 shrink-0 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-xs text-rose-300">{docPromptError}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setDocOpen(false);
                  setDocData(null);
                  setDocPromptOpen(false);
                  setDocPrompt('');
                  setDocPromptError('');
                }}
                className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDocSave}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-700 to-sky-500 text-white text-sm font-semibold hover:from-indigo-800 hover:to-sky-600 transition shadow-sm"
              >
                Save Document
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          PICTURE MODAL
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={pictureOpen}
        onClose={closePictureModal}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <DialogPanel className="w-full sm:max-w-3xl bg-white shadow-2xl border-t sm:border border-indigo-100 rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[95dvh] sm:max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-900 to-sky-600 px-4 sm:px-6 py-4 flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-white font-semibold">Edit Picture</h2>
                <p className="text-indigo-200 text-xs mt-0.5">
                  Add a URL, upload, or generate with AI
                </p>
              </div>
              <button onClick={closePictureModal} className="text-white/70 hover:text-white transition p-1 -mr-1">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
            <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">Title</label>
                  <input
                    type="text"
                    value={pictureData?.title || ''}
                    onChange={(e) => setPictureData((d) => ({ ...d, title: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    placeholder="Picture title"
                  />
                </div>

                {/* ── Section A: AI Image Generation ── */}
                <div className="rounded-2xl overflow-hidden border border-indigo-200/50 shadow-md">
                  <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-violet-900 via-indigo-800 to-sky-700">
                    <span className="flex items-center justify-center h-7 w-7 rounded-lg bg-white/10 ring-1 ring-white/20">
                      <SparklesIcon className="h-4 w-4 text-violet-200" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-white leading-none">Generate with AI</span>
                      <span className="block text-xs text-violet-300 mt-0.5">Describe the image you want</span>
                    </span>
                  </div>
                  <div className="bg-gradient-to-br from-violet-950/95 to-indigo-950 p-4 space-y-3">
                    {pendingGeneratedUrl ? (
                      <>
                        <div className="w-full rounded-xl overflow-hidden border border-violet-400/30 bg-black/20">
                          <img src={pendingGeneratedUrl} alt="AI generated" className="w-full h-40 object-cover" />
                        </div>
                        <p className="text-violet-300 text-xs text-center">Image generated — save it to the card or regenerate</p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveGeneratedImage}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-xs font-bold shadow-lg transition-all active:scale-95"
                          >
                            <CheckIcon className="h-3.5 w-3.5" />
                            Save Image
                          </button>
                          <button
                            onClick={handleDiscardGeneratedImage}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-violet-400/40 text-violet-200 text-xs font-semibold hover:bg-white/10 transition-all active:scale-95"
                          >
                            <SparklesIcon className="h-3.5 w-3.5" />
                            Regenerate
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {pictureData?.imageUrl && (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sky-500/15 border border-sky-400/30 text-sky-300 text-xs">
                            <PhotoIcon className="h-3.5 w-3.5 shrink-0" />
                            <span>Current image will be used as reference</span>
                          </div>
                        )}
                        <div className="relative">
                          <input
                            type="text"
                            value={picturePrompt}
                            onChange={(e) => setPicturePrompt(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !pictureGenerating && !pictureUploading) handlePictureGenerate(); }}
                            className="w-full rounded-xl border border-violet-500/40 bg-white/5 px-4 py-3 pr-28 text-sm text-violet-50 placeholder:text-violet-400/60 focus:ring-2 focus:ring-violet-400 focus:border-violet-400 outline-none"
                            placeholder={pictureData?.imageUrl ? 'e.g. "Add a sunset background"…' : 'A futuristic office with neon lights…'}
                            disabled={pictureGenerating || pictureUploading}
                          />
                          <button
                            onClick={handlePictureGenerate}
                            disabled={pictureGenerating || pictureUploading}
                            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-400 hover:to-indigo-400 text-white text-xs font-bold shadow-lg shadow-violet-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                          >
                            {pictureGenerating ? (
                              <>
                                <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                </svg>
                                Working…
                              </>
                            ) : (
                              <>
                                <SparklesIcon className="h-3.5 w-3.5" />
                                Generate
                              </>
                            )}
                          </button>
                        </div>
                        {pictureGenerating && (
                          <div className="flex items-center gap-2 text-violet-300 text-xs">
                            <svg className="h-3.5 w-3.5 animate-spin shrink-0" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            <span>Generating your image, this may take a moment…</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* ── Section B: Upload or URL ── */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <PhotoIcon className="h-4 w-4 text-indigo-400" />
                    Upload or paste a URL
                  </p>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Image URL</label>
                    <input
                      type="url"
                      value={
                        pictureData?.imageSource === 'url' && pictureData?.imageUrl
                          ? pictureData.imageUrl
                          : ''
                      }
                      onChange={(e) => setPictureData((d) => ({ ...d, imageUrl: e.target.value, imageSource: 'url' }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                      placeholder="https://example.com/image.png"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Upload file</label>
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-100 cursor-pointer transition">
                      <PhotoIcon className="h-4 w-4" />
                      {pictureUploading ? 'Uploading...' : 'Choose file'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={handlePictureUpload}
                        disabled={pictureUploading || pictureGenerating}
                    />
                  </label>
                  </div>
                  {pictureData?.imageSource === 'upload' && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-700 mt-1">
                      <CheckIcon className="h-3.5 w-3.5 shrink-0" />
                      <span>Image uploaded</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">Description</label>
                  <textarea
                    rows={3}
                    value={pictureData?.description || ''}
                    onChange={(e) => setPictureData((d) => ({ ...d, description: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
                    placeholder="Notes about this image..."
                  />
                </div>

                {pictureError && (
                  <p className="text-xs text-rose-600">{pictureError}</p>
                )}
              </div>

              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-900">Preview</p>
                <div className="w-full rounded-xl border border-gray-200 bg-gray-50 overflow-hidden min-h-[220px] flex items-center justify-center">
                  {(pendingGeneratedUrl || resolveApiAssetUrl(pictureData?.imageUrl)) ? (
                    <img
                      src={pendingGeneratedUrl || resolveApiAssetUrl(pictureData?.imageUrl)}
                      alt={pictureData?.title || 'Picture preview'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-gray-400 text-sm flex items-center gap-2">
                      <PhotoIcon className="h-5 w-5" />
                      <span>No image selected</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={handlePictureDownload}
                  disabled={!resolveApiAssetUrl(pictureData?.imageUrl)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" /> Download
                </button>
              </div>
            </div>
            </div>{/* end overflow-y-auto */}

            <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 shrink-0">
              <button
                onClick={closePictureModal}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handlePictureSave}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-700 to-sky-500 text-white text-sm font-semibold hover:from-indigo-800 hover:to-sky-600 transition shadow-sm"
              >
                Save Picture
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          ENGAGEMENT MODAL (likes / poll / comments)
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={engagementOpen} onClose={() => setEngagementOpen(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-4">
          <DialogPanel className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-indigo-100 overflow-hidden max-h-[85dvh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-900 to-sky-600 px-5 py-4 flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-white font-semibold text-sm">{engagementEl?.title || 'Element'}</h2>
                <p className="text-indigo-200 text-xs mt-0.5">Likes · Poll · Comments</p>
              </div>
              <button onClick={() => setEngagementOpen(false)} className="text-white/70 hover:text-white transition">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-5">
              {/* Poll */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Poll</p>
                <div className="flex gap-2">
                  {['yes','no'].map(vote => {
                    const count = (engagementEl?.poll?.[vote] || []).length;
                    const voted = (engagementEl?.poll?.[vote] || []).includes(currentUserId);
                    return (
                      <button key={vote} onClick={() => handleVotePoll(engagementEl?.id, voted ? null : vote)}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border transition ${
                          voted
                            ? vote === 'yes' ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'bg-rose-50 border-rose-400 text-rose-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}>
                        {vote === 'yes' ? '✓' : '✗'} {vote === 'yes' ? 'Yes' : 'No'} <span className="font-normal text-xs">({count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Likes */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Likes</p>
                <button onClick={() => handleToggleLike(engagementEl?.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border transition ${
                    (engagementEl?.likes || []).includes(currentUserId)
                      ? 'bg-rose-50 border-rose-300 text-rose-600'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}>
                  <HeartIcon className="h-4 w-4" />
                  {(engagementEl?.likes || []).length} {(engagementEl?.likes || []).length === 1 ? 'like' : 'likes'}
                </button>
              </div>

              {/* Comments */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Comments ({(engagementEl?.comments || []).length})</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(engagementEl?.comments || []).map(c => (
                    <div key={c.id} className="flex items-start gap-2 p-2.5 rounded-xl bg-gray-50 border border-gray-100">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-700">{c.authorName}</p>
                        <p className="text-sm text-gray-800 mt-0.5 break-words">{c.text}</p>
                      </div>
                      {(Number(c.authorId) === currentUserId || user?.access === 'admin' || user?.Access === 'admin') && (
                        <button onClick={() => handleDeleteComment(c.id)} className="text-gray-400 hover:text-rose-500 transition shrink-0">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {(engagementEl?.comments || []).length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-3">No comments yet</p>
                  )}
                </div>
              </div>

              {engagementError && <p className="text-xs text-rose-600">{engagementError}</p>}
            </div>

            {/* Add comment */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 shrink-0 flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && newComment.trim()) handleAddComment(); }}
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                placeholder="Add a comment…"
                disabled={engagementLoading}
              />
              <button onClick={handleAddComment} disabled={!newComment.trim() || engagementLoading}
                className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition">
                Post
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          ELEMENT DETAIL MODAL
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setDetailData(null); }}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-4">
          <DialogPanel className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-indigo-100 overflow-hidden max-h-[95dvh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-900 to-sky-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-bold text-white shrink-0"
                  style={{ background: detailData?.color?.border }}
                >
                  {detailData?.number}
                </span>
                <div>
                  <h2 className="text-white font-semibold">Edit Element</h2>
                  <p className="text-indigo-200 text-xs mt-0.5 capitalize">
                    {detailData?.type} · created by {detailData?.createdBy}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setDetailOpen(false); setDetailData(null); }}
                className="text-white/70 hover:text-white transition"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">Title</label>
                <input
                  type="text"
                  value={detailData?.title || ''}
                  onChange={(e) => setDetailData((d) => ({ ...d, title: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  placeholder="Give this element a title..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">Description</label>
                <textarea
                  rows={4}
                  value={detailData?.description || ''}
                  onChange={(e) => setDetailData((d) => ({ ...d, description: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none transition"
                  placeholder="Add notes, ideas, or context..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Colour</label>
                <div className="flex items-center gap-2.5 flex-wrap">
                  {PRESET_COLORS.map((c) => {
                    const active = detailData?.color?.label === c.label;
                    return (
                      <button
                        key={c.label}
                        title={c.label}
                        onClick={() => setDetailData((d) => ({ ...d, color: c }))}
                        className="h-9 w-9 rounded-full border-2 transition-transform hover:scale-110 flex items-center justify-center"
                        style={{
                          background: c.bg,
                          borderColor: active ? c.border : '#e2e8f0',
                          boxShadow: active ? `0 0 0 3px ${c.border}40` : 'none',
                        }}
                      >
                        {active && <CheckIcon className="h-4 w-4" style={{ color: c.border }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => { setDetailOpen(false); setDetailData(null); }}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDetailSave}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-700 to-sky-500 text-white text-sm font-semibold hover:from-indigo-800 hover:to-sky-600 transition shadow-sm"
              >
                Save Changes
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          INVITE STAFF MODAL
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-4">
          <DialogPanel className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-indigo-100 overflow-hidden max-h-[95dvh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-900 to-sky-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-semibold">Invite Staff</h2>
                <p className="text-indigo-200 text-xs mt-0.5">
                  Add collaborators to this brainstorm
                </p>
              </div>
              <button onClick={() => setInviteOpen(false)} className="text-white/70 hover:text-white transition">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Product Owner selection */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                  <StarIcon className="h-3.5 w-3.5 text-amber-500" />
                  Product Owner
                </label>
                <select
                  value={productOwner?.id || ''}
                  onChange={(e) => {
                    const staff = staffOptions.find(s => String(s.id) === e.target.value);
                    handleSetProductOwner(staff || null);
                  }}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                >
                  <option value="">No product owner</option>
                  {staffOptions.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Staff list */}
              <div className="space-y-2">
                {isLoadingStaff && (
                  <p className="text-sm text-gray-500">Loading staff...</p>
                )}
                {!isLoadingStaff && staffOptions.length === 0 && (
                  <p className="text-sm text-gray-500">No staff found for invite.</p>
                )}
                {staffOptions.map((s) => {
                  const invited = invitedStaff.some((entry) => entry.id === s.id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggleStaff(s)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition ${
                        invited
                          ? 'bg-indigo-50 border-indigo-300 shadow-sm'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
                          style={{ background: invited ? '#6366f1' : '#94a3b8' }}
                        >
                          {s.name.split(' ').map((n) => n[0]).join('')}
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-semibold text-gray-900">{s.name}</p>
                          <p className="text-xs text-gray-500">{s.role}</p>
                        </div>
                      </div>
                      <div
                        className={`h-5 w-5 rounded-full border-2 flex items-center justify-center transition ${
                          invited ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
                        }`}
                      >
                        {invited && <CheckIcon className="h-3 w-3 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Email invite */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Or invite by email
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  />
                  <button className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition">
                    <PlusIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Copy invite link */}
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-indigo-300 text-indigo-600 text-sm font-medium hover:bg-indigo-50 transition"
              >
                <LinkIcon className="h-4 w-4" />
                {inviteCopied ? '✓ Link copied!' : 'Copy invite link'}
              </button>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {invitedStaff.length > 0
                  ? `${invitedStaff.length} staff member${invitedStaff.length > 1 ? 's' : ''} selected`
                  : 'No staff selected yet'}
              </p>
              <button
                onClick={handleInviteDone}
                disabled={inviteSubmitting}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-700 to-sky-500 text-white text-sm font-semibold hover:from-indigo-800 hover:to-sky-600 transition shadow-sm"
              >
                {inviteSubmitting ? 'Sending...' : 'Done'}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          NEW BOARD MODAL
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={newBoardOpen} onClose={closeNewBoardModal} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-4">
          <DialogPanel className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-indigo-100 overflow-hidden max-h-[90dvh] flex flex-col">

            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-900 to-sky-600 px-5 py-4 flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-white font-semibold text-base">New Brainstorm Board</h2>
                <p className="text-indigo-200 text-xs mt-0.5">Start blank or let an AI agent generate your board content</p>
              </div>
              <button onClick={closeNewBoardModal} disabled={newBoardCreating} className="text-white/70 hover:text-white transition disabled:opacity-40">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-5">

              {/* ── Mode selector cards ────────────────────────────────── */}
              <div className="grid grid-cols-2 gap-3">
                {/* Blank */}
                <button
                  onClick={() => setNewBoardMode('blank')}
                  className={`relative flex flex-col items-start gap-2 p-4 rounded-2xl border-2 text-left transition-all ${
                    newBoardMode === 'blank'
                      ? 'border-indigo-400 bg-indigo-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-gray-50'
                  }`}
                >
                  {newBoardMode === 'blank' && (
                    <span className="absolute top-2.5 right-2.5 w-4 h-4 rounded-full bg-indigo-600 flex items-center justify-center">
                      <CheckIcon className="h-2.5 w-2.5 text-white" />
                    </span>
                  )}
                  <span className="text-2xl">🗒</span>
                  <div>
                    <p className={`text-sm font-bold ${newBoardMode === 'blank' ? 'text-indigo-800' : 'text-gray-900'}`}>Blank Board</p>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">Start with an empty canvas. Add sticky notes, docs and images yourself.</p>
                  </div>
                </button>

                {/* AI Generate */}
                <button
                  onClick={() => setNewBoardMode('ai')}
                  className={`relative flex flex-col items-start gap-2 p-4 rounded-2xl border-2 text-left transition-all ${
                    newBoardMode === 'ai'
                      ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-sky-50 shadow-sm'
                      : 'border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-sky-50/60 hover:border-indigo-400'
                  }`}
                >
                  {newBoardMode === 'ai' && (
                    <span className="absolute top-2.5 right-2.5 w-4 h-4 rounded-full bg-indigo-600 flex items-center justify-center">
                      <CheckIcon className="h-2.5 w-2.5 text-white" />
                    </span>
                  )}
                  <span className="text-2xl">✨</span>
                  <div>
                    <p className={`text-sm font-bold ${newBoardMode === 'ai' ? 'text-indigo-800' : 'text-indigo-700'}`}>AI Generate</p>
                    <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">Pick a specialist AI agent and it will build a complete board for you.</p>
                  </div>
                  <span className="mt-auto inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 bg-white border border-indigo-200 rounded-full px-2 py-0.5">
                    <SparklesIcon className="h-3 w-3" /> 6 agents available
                  </span>
                </button>
              </div>

              {/* ── Board title (shared) ───────────────────────────────── */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">Board Title</label>
                <input
                  type="text"
                  value={newBoardTitle}
                  onChange={(e) => setNewBoardTitle(e.target.value)}
                  placeholder={
                    newBoardMode === 'ai' && selectedAgent
                      ? `${AI_AGENTS.find((a) => a.id === selectedAgent)?.title} Board`
                      : 'My Brainstorm Board'
                  }
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>

              {/* ── AI mode: agent grid + agent inputs ────────────────── */}
              {newBoardMode === 'ai' && (
                <div className="space-y-4">

                  {/* Agent selector */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Choose an agent</p>
                    <div className="grid grid-cols-2 gap-2">
                      {AI_AGENTS.map((agent) => (
                        <button
                          key={agent.id}
                          onClick={() => { setSelectedAgent(agent.id); setAgentInputs({}); setAgentFiles({}); setNewBoardError(''); }}
                          className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                            selectedAgent === agent.id
                              ? 'bg-gradient-to-br from-indigo-50 to-sky-50 border-indigo-300 shadow-sm ring-1 ring-indigo-200'
                              : 'bg-white border-gray-200 hover:border-indigo-200 hover:bg-gray-50'
                          }`}
                        >
                          <span className="text-2xl shrink-0 mt-0.5">{agent.emoji}</span>
                          <div className="min-w-0">
                            <p className={`text-xs font-bold leading-snug ${selectedAgent === agent.id ? 'text-indigo-700' : 'text-gray-900'}`}>
                              {agent.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{agent.description}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Agent details */}
                  {selectedAgent && (() => {
                    const agent = AI_AGENTS.find((a) => a.id === selectedAgent);
                    return (
                      <div className="space-y-4">

                        {/* Outputs preview */}
                        <div className="rounded-xl bg-gradient-to-r from-indigo-50 to-sky-50 border border-indigo-100 p-3">
                          <p className="text-xs font-semibold text-indigo-700 mb-2">✨ This agent will generate:</p>
                          <div className="flex flex-wrap gap-1.5">
                            {agent.outputs.map((output) => (
                              <span key={output} className="text-xs bg-white border border-indigo-200 text-indigo-700 rounded-full px-2.5 py-0.5 shadow-sm">
                                {output}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Agent-specific inputs */}
                        <div className="space-y-3">
                          {agent.inputs.map((field) => (
                            <div key={field.key}>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                {field.label}{' '}
                                {field.optional
                                  ? <span className="font-normal text-gray-400">(optional)</span>
                                  : <span className="text-rose-500">*</span>}
                              </label>

                              {field.type === 'textarea' ? (
                                <textarea
                                  rows={3}
                                  value={agentInputs[field.key] || ''}
                                  onChange={(e) => setAgentInputs((p) => ({ ...p, [field.key]: e.target.value }))}
                                  placeholder={field.placeholder}
                                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none bg-white"
                                />
                              ) : field.type === 'select' ? (
                                <select
                                  value={agentInputs[field.key] || ''}
                                  onChange={(e) => setAgentInputs((p) => ({ ...p, [field.key]: e.target.value }))}
                                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                                >
                                  <option value="">Select…</option>
                                  {field.options.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              ) : field.type === 'multiselect' ? (
                                <div className="flex flex-wrap gap-2">
                                  {field.options.map((opt) => {
                                    const isSelected = (agentInputs[field.key] || []).includes(opt);
                                    return (
                                      <button
                                        key={opt}
                                        type="button"
                                        onClick={() =>
                                          setAgentInputs((p) => {
                                            const arr = p[field.key] || [];
                                            return { ...p, [field.key]: isSelected ? arr.filter((v) => v !== opt) : [...arr, opt] };
                                          })
                                        }
                                        className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition ${
                                          isSelected
                                            ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                                            : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-200 hover:bg-gray-50'
                                        }`}
                                      >
                                        {opt}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : field.type === 'file' ? (
                                <label className={`flex items-center gap-3 w-full rounded-xl border-2 border-dashed px-4 py-3 cursor-pointer transition ${
                                  agentFiles[field.key]?.length
                                    ? 'border-indigo-300 bg-indigo-50'
                                    : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-gray-50'
                                }`}>
                                  <ArrowDownTrayIcon className="h-5 w-5 shrink-0 text-indigo-400" />
                                  <span className="min-w-0 text-sm truncate flex-1">
                                    {agentFiles[field.key]?.length
                                      ? <span className="text-indigo-700 font-medium">{agentFiles[field.key].length === 1 ? agentFiles[field.key][0].name : `${agentFiles[field.key].length} files selected`}</span>
                                      : <span className="text-gray-400">Upload files — PDF, DOCX, TXT, JPG, PNG…</span>}
                                  </span>
                                  {agentFiles[field.key]?.length > 0 && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.preventDefault(); setAgentFiles((p) => { const n = { ...p }; delete n[field.key]; return n; }); }}
                                      className="ml-auto shrink-0 text-gray-400 hover:text-rose-500 transition"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                  <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt,.md,.csv,.jpg,.jpeg,.png"
                                    multiple
                                    className="sr-only"
                                    onChange={(e) => {
                                      const files = Array.from(e.target.files || []);
                                      if (files.length) setAgentFiles((p) => ({ ...p, [field.key]: files }));
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              ) : (
                                <input
                                  type={field.type}
                                  value={agentInputs[field.key] || ''}
                                  onChange={(e) => setAgentInputs((p) => ({ ...p, [field.key]: e.target.value }))}
                                  placeholder={field.placeholder}
                                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {aiJobId && aiJobProgress && (
                <BrainstormJobStatus
                  jobId={aiJobId}
                  progress={aiJobProgress}
                  onCancel={() => {
                    setAiJobPolling(false);
                    setAiJobId(null);
                    setAiJobProgress(null);
                    setNewBoardCreating(false);
                  }}
                />
              )}

              {newBoardError && (
                <p className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2.5">{newBoardError}</p>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 shrink-0">
              <button
                onClick={closeNewBoardModal}
                disabled={newBoardCreating}
                className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-100 transition disabled:opacity-50"
              >
                Cancel
              </button>
              {newBoardMode === 'blank' ? (
                <button
                  onClick={handleCreateBlankBoard}
                  disabled={newBoardCreating}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-indigo-700 to-sky-500 text-white text-sm font-semibold hover:from-indigo-800 hover:to-sky-600 transition shadow-sm disabled:opacity-60"
                >
                  {newBoardCreating ? 'Creating…' : 'Create Board'}
                </button>
              ) : (
                <button
                  onClick={handleAiGenerateBoard}
                  disabled={newBoardCreating || !selectedAgent}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-indigo-700 to-sky-500 text-white text-sm font-semibold hover:from-indigo-800 hover:to-sky-600 transition shadow-sm disabled:opacity-60 flex items-center gap-1.5"
                >
                  {newBoardCreating
                    ? <><SparklesIcon className="h-4 w-4 animate-pulse" /> Generating…</>
                    : <><SparklesIcon className="h-4 w-4" /> Generate Board</>}
                </button>
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}

// ─── Small toolbar button helper ──────────────────────────────────────────────
function ToolbarBtn({ onClick, colors, icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition ${colors}`}
    >
      <span className="text-base leading-none">{icon}</span>
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
